import React, { useState, useEffect, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import GridViewIcon from '@mui/icons-material/GridView';
import Paper from '@mui/material/Paper';
import { styled, alpha } from '@mui/material/styles';
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import companyService from '../../../services/CompanyService';
import adminRoleService from '../../../services/AdminRoleService';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import DynamicTabs from '../../common/DynamicTabs';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import policyService from '../../../services/PoliciesService';
import appPasswordService from '../../../services/PasswordService';
import DonutChart from '../../common/DonutChart';
import Modal from '../../common/Modal';
import NormalTextField from '../../common/NormalTextField';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { gridColumnVisibilityModelSelector } from '@mui/x-data-grid';
import {CircularProgress} from '@mui/material';
import { format } from 'date-fns';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import CardActionArea from '@mui/material/CardActionArea';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { pieArcLabelClasses } from '@mui/x-charts/PieChart';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedIcon from '@mui/icons-material/Verified';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RefreshIcon from '@mui/icons-material/Refresh';
import GppBadIcon from '@mui/icons-material/GppBad';
import FilterListIcon from '@mui/icons-material/FilterList';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import LogoLoader from '../../common/LogoLoader';
import CancelIcon from '@mui/icons-material/Cancel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/Warning';
import HRService from '../../../services/HrService';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

// Format date for API calls
const formatDateForAPI = (date) => {
    // Check if date is valid before formatting
    if (date instanceof Date && !isNaN(date)) {
        return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    }
    // Return current date as fallback for invalid dates
    return new Date().toISOString().split('T')[0];
};

const DataTable = React.lazy(() => import('../../common/DataGrid'));

// Define styled components with a default theme approach
const DataTableContainer = styled(mui.Paper)({
  width: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '& .MuiDataGrid-root': {
    border: 'none',
    '& .MuiDataGrid-cell': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '& .MuiDataGrid-columnsContainer': {
      backgroundColor: '#fff',
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    },
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 600,
      color: '#2c3e50',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
    },
  },
});

// Custom styled components with simpler styles that don't rely on theme
const StatsCard = styled(mui.Paper)(({ gradient, color = 'primary' }) => ({
  padding: '20px',
  borderRadius: '8px',
  height: '100%',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  background: '#fff',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
  },
}));

const ChartContainer = styled(mui.Paper)({
  padding: '16px',
  borderRadius: '8px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  height: '100%',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
  },
});

const SectionTitle = styled(mui.Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#2c3e50',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    marginRight: '8px',
    fontSize: '1.2rem',
    color: '#0288d1',
  },
});

const CategoryButton = styled(mui.Button)(({ active }) => ({
  margin: '0 5px 10px 0',
  borderRadius: '6px',
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: active ? 600 : 400,
  fontSize: '0.875rem',
  backgroundColor: active ? '#0288d1' : 'rgba(0, 0, 0, 0.05)',
  color: active ? '#fff' : '#2c3e50',
  '&:hover': {
    backgroundColor: active ? '#0277bd' : 'rgba(0, 0, 0, 0.1)',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
  },
}));

const StatsValue = styled(mui.Typography)(({ color }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: color || '#0288d1',
  marginTop: '4px',
}));

const StatsLabel = styled(mui.Typography)(({ color }) => ({
  fontSize: '0.8rem',
  color: color || '#7f8c8d',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const DataCard = styled(mui.Paper)({
  padding: '20px',
  borderRadius: '8px',
  minHeight: '250px',
  height: '100%',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
  },
});

const StatusChip = styled(mui.Chip)(({ status }) => {
  let color = '#2196f3';
  let bgColor = alpha('#2196f3', 0.12);
  let borderColor = alpha('#2196f3', 0.3);
  
  if (status === 'Compliant') {
    color = '#2e7d32'; // Green
    bgColor = alpha('#2e7d32', 0.12);
    borderColor = alpha('#2e7d32', 0.3);
  } else if (status === 'Needs Review') {
    color = '#fb8c00'; // Orange
    bgColor = alpha('#fb8c00', 0.12);
    borderColor = alpha('#fb8c00', 0.3);
  } else if (status === 'Incomplete Setup' || status === 'No Data') {
    color = '#757575'; // Grey
    bgColor = alpha('#757575', 0.12);
    borderColor = alpha('#757575', 0.3);
  }
  
  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    height: '24px',
    fontSize: '0.7rem',
    border: `1px solid ${borderColor}`,
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: '0.85rem',
    },
    '& .MuiChip-label': {
      padding: '0 8px',
    }
  };
});

// Add more styles for the data tables
const StyledDataGrid = styled('div')({
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: 'rgba(2, 136, 209, 0.04)',
    padding: '0 8px',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    backgroundColor: 'rgba(2, 136, 209, 0.02)',
  },
  '& .MuiDataGrid-cell': {
    fontSize: '0.8rem',
    padding: '0 8px',
  },
});

const ComplianceBadge = styled('div')(({ count, type }) => {
  let bgColor = alpha('#2196f3', 0.12);
  let color = '#2196f3';
  
  if (type === 'violation') {
    bgColor = alpha('#f44336', 0.12);
    color = '#f44336';
  } else if (type === 'warning') {
    bgColor = alpha('#fb8c00', 0.12);
    color = '#fb8c00';
  } else if (type === 'success') {
    bgColor = alpha('#2e7d32', 0.12);
    color = '#2e7d32';
  }
  
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    minWidth: '22px',
    height: '22px',
    padding: '0 8px',
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: bgColor,
    color: color,
    marginLeft: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  };
});

// Admin Roles Manager Component
const AdminRolesManager = ({ apps, theme }) => {
  const [selectedApp, setSelectedApp] = useState(null);
  const [appFilter, setAppFilter] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [adminRoles, setAdminRoles] = useState([]);
  const [adminRolesByApp, setAdminRolesByApp] = useState({});
  const [usersWithRole, setUsersWithRole] = useState([]);
  const [userStatusMap, setUserStatusMap] = useState({});
  const [selectedAdminRole, setSelectedAdminRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: 'info', show: false });
  const [expandedApp, setExpandedApp] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  
  // Organize admin roles by application
  useEffect(() => {
    const organizeByApp = () => {
      const rolesByApp = {};
      
      if (adminRoles.length > 0) {
        adminRoles.forEach(role => {
          const appId = role.APP_NAME;
          if (!rolesByApp[appId]) {
            rolesByApp[appId] = [];
          }
          rolesByApp[appId].push(role);
        });
      }
      
      setAdminRolesByApp(rolesByApp);
    };
    
    organizeByApp();
  }, [adminRoles]);
  
  // Load all admin roles for all apps on initial load
  useEffect(() => {
    const loadAllAdminRoles = async () => {
      if (apps && apps.length > 0) {
        setLoading(true);
        try {
          const adminRolesData = [];
          
          for (const app of apps) {
            try {
              const response = await adminRoleService.getAdminRolesByApp(app.id);
              if (response && response.length > 0) {
                // Enhance with app name for display
                const rolesWithAppName = response.map(role => ({
                  ...role,
                  appName: app.APP_NAME
                }));
                adminRolesData.push(...rolesWithAppName);
              }
            } catch (error) {
              console.error(`Error loading roles for app ${app.APP_NAME}:`, error);
            }
          }
          
          setAdminRoles(adminRolesData);
        } catch (error) {
          console.error('Error loading all admin roles:', error);
          setMessage({
            text: 'Error loading admin roles data. Please try again.',
            type: 'error',
            show: true
          });
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadAllAdminRoles();
  }, [apps]);

  // Handle app selection
  const handleAppChange = async (event) => {
    const appId = event.target.value;
    setSelectedApp(appId);
    setSearchQuery('');
    
    if (appId) {
      try {
        setLoading(true);
        // Get unique roles from the application
        const uniqueRolesResponse = await adminRoleService.getUniqueRolesByApp(appId);
        if (uniqueRolesResponse && uniqueRolesResponse.roles) {
          setAvailableRoles(uniqueRolesResponse.roles.map(role => ({ 
            id: role, 
            name: role 
          })));
        }
      } catch (error) {
        console.error('Error loading roles:', error);
        setMessage({ 
          text: 'Error loading roles. Please try again.', 
          type: 'error', 
          show: true 
        });
      } finally {
        setLoading(false);
      }
    } else {
      setAvailableRoles([]);
    }
  };

  // Handle setting a role as admin
  const handleAddAdminRole = async (roleName) => {
    if (!selectedApp || !roleName) return;
    
    try {
      setRoleLoading(true);
      const response = await adminRoleService.createAdminRole({
        APP_NAME: selectedApp,
        ROLE_NAME: roleName
      });
      
      // Add the new admin role to the list
      if (response) {
        // Find app name for display
        const app = apps.find(a => a.id === selectedApp);
        const appName = app ? app.APP_NAME : 'Unknown App';
        
        const enhancedRole = {
          ...response,
          appName
        };
        
        setAdminRoles(prev => [...prev, enhancedRole]);
        setMessage({ 
          text: `Role "${roleName}" added as an administrator role.`, 
          type: 'success', 
          show: true 
        });
      }
    } catch (error) {
      console.error('Error adding admin role:', error);
      setMessage({ 
        text: 'Error adding admin role. Please try again.', 
        type: 'error', 
        show: true 
      });
    } finally {
      setRoleLoading(false);
    }
  };

  // Show confirmation dialog before removing a role
  const confirmRemoveAdminRole = (role) => {
    setRoleToDelete(role);
    setOpenConfirmDialog(true);
  };

  // Handle closing the confirmation dialog
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setRoleToDelete(null);
  };

  // Handle removing a role from admin roles (after confirmation)
  const handleRemoveAdminRole = async () => {
    if (!roleToDelete) return;
    
    const roleId = roleToDelete.id;
    try {
      setRoleLoading(true);
      await adminRoleService.deleteAdminRole(roleId);
      
      // Remove the role from the admin roles list
      setAdminRoles(prev => prev.filter(role => role.id !== roleId));
      
      // If the removed role was selected, clear the selection
      if (selectedAdminRole && selectedAdminRole.id === roleId) {
        setSelectedAdminRole(null);
        setUsersWithRole([]);
      }
      
      setMessage({ 
        text: 'Admin role removed successfully.', 
        type: 'success', 
        show: true 
      });
    } catch (error) {
      console.error('Error removing admin role:', error);
      setMessage({ 
        text: 'Error removing admin role. Please try again.', 
        type: 'error', 
        show: true 
      });
    } finally {
      setRoleLoading(false);
      handleCloseConfirmDialog();
    }
  };

  // Handle viewing users with a specific admin role
  const handleViewUsers = async (role) => {
    setSelectedAdminRole(role);
    
    try {
      setUserLoading(true);
      const usersResponse = await adminRoleService.getUsersWithAdminRole(role.id);
      
      if (usersResponse) {
        console.log(`Got ${usersResponse.length} users for role: ${role.ROLE_NAME}`);
        
        try {
          // First try to fetch all HR records at once for efficiency
          const allHrRecords = await HRService.fetchAllHRRecords();
          console.log(`Pre-fetched ${allHrRecords.length} HR records for lookup`);
          
          // Create a map of email -> record for fast lookups
          const hrRecordsByEmail = {};
          allHrRecords.forEach(record => {
            if (record.EMAIL_ADDRESS) {
              // Store with normalized email (lowercase and trimmed)
              const normalizedEmail = record.EMAIL_ADDRESS.toLowerCase().trim();
              hrRecordsByEmail[normalizedEmail] = record;
            }
          });
          
          // Get unique email domains for diagnostics
          const emailDomains = new Set();
          Object.keys(hrRecordsByEmail).forEach(email => {
            const domain = email.split('@')[1];
            if (domain) emailDomains.add(domain);
          });
          console.log(`HR records contain these email domains:`, Array.from(emailDomains));
          
          // Process each user
          const usersWithStatus = await Promise.all(
            usersResponse.map(async (user) => {
              console.log(`Checking HR status for user: ${user.EMAIL_ADDRESS}`);
              
              try {
                // Normalize email for lookup - lowercase and trim
                const normalizedEmail = user.EMAIL_ADDRESS ? user.EMAIL_ADDRESS.toLowerCase().trim() : '';
                const userDomain = normalizedEmail.split('@')[1];
                
                if (!normalizedEmail) {
                  console.warn('User has no email address to check against HR records');
                  return { ...user, HR_STATUS: 'Not Found', HR_LOOKUP_ERROR: 'No email address' };
                }
                
                // First try direct map lookup (most efficient)
                let hrRecord = hrRecordsByEmail[normalizedEmail];
                
                // If not found, try the API method which has fallback search
                if (!hrRecord) {
                  console.log(`No match found in pre-fetched records for ${normalizedEmail}, trying API lookup`);
                  try {
                    hrRecord = await HRService.fetchHRRecordByEmail(normalizedEmail);
                  } catch (apiError) {
                    console.warn(`API lookup failed for ${normalizedEmail}:`, apiError.message);
                  }
                }
                
                // Simplified status handling - only care if user is Active, everything else is Not Found
                if (hrRecord && hrRecord.STATUS && hrRecord.STATUS.toUpperCase() === 'ACTIVE') {
                  console.log(`✓ Found ACTIVE HR record for ${normalizedEmail}`);
                  return {
                    ...user,
                    HR_STATUS: 'Active',
                    HR_RECORD_FOUND: true
                  };
                } else if (hrRecord && hrRecord.STATUS) {
                  // User exists in HR but has inactive status - treat as Not Found
                  console.log(`Found HR record for ${normalizedEmail} but status is not Active: ${hrRecord.STATUS}`);
                  return {
                    ...user,
                    HR_STATUS: 'Not Found',
                    HR_RECORD_FOUND: false,
                    HR_ACTUAL_STATUS: hrRecord.STATUS // Store actual status just for debugging
                  };
                } else {
                  // Try to find a fuzzy match as last resort
                  const potentialMatches = Object.entries(hrRecordsByEmail)
                    .filter(([email, record]) => {
                      // Check if email has same domain and similar local part
                      const emailDomain = email.split('@')[1];
                      return emailDomain === userDomain && 
                             (email.includes(normalizedEmail) || normalizedEmail.includes(email));
                    })
                    .map(([email, record]) => ({ email, record }));
                  
                  if (potentialMatches.length > 0) {
                    console.log(`Found ${potentialMatches.length} potential fuzzy matches for ${normalizedEmail}`);
                    
                    // Use the first potential match - only care if it's Active
                    const match = potentialMatches[0].record;
                    
                    if (match.STATUS && match.STATUS.toUpperCase() === 'ACTIVE') {
                      return {
                        ...user,
                        HR_STATUS: 'Active',
                        HR_RECORD_FOUND: true,
                        HR_FUZZY_MATCH: true,
                        HR_MATCHED_EMAIL: potentialMatches[0].email
                      };
                    } else {
                      // Fuzzy match but not active - treat as Not Found
                      return {
                        ...user,
                        HR_STATUS: 'Not Found',
                        HR_RECORD_FOUND: false,
                        HR_FUZZY_MATCH: true,
                        HR_MATCHED_EMAIL: potentialMatches[0].email,
                        HR_ACTUAL_STATUS: match.STATUS // Store actual status just for debugging
                      };
                    }
                  }
                  
                  // Normal case - truly not found
                  console.warn(`No HR record found for ${normalizedEmail}`);
                  return {
                    ...user,
                    HR_STATUS: 'Not Found',
                    HR_RECORD_FOUND: false
                  };
                }
              } catch (error) {
                console.error(`Error checking HR status for ${user.EMAIL_ADDRESS}:`, error);
                return {
                  ...user,
                  HR_STATUS: 'Error',
                  HR_RECORD_FOUND: false,
                  HR_LOOKUP_ERROR: error.message
                };
              }
            })
          );
          
          // Log summary of HR status check results
          const notFoundCount = usersWithStatus.filter(u => u.HR_STATUS === 'Not Found').length;
          const inactiveCount = usersWithStatus.filter(u => u.HR_STATUS !== 'Active' && u.HR_STATUS !== 'Not Found' && u.HR_STATUS !== 'Error').length;
          const errorCount = usersWithStatus.filter(u => u.HR_STATUS === 'Error').length;
          const fuzzyMatchCount = usersWithStatus.filter(u => u.HR_FUZZY_MATCH).length;
          
          console.log(`HR status check results - Total users: ${usersWithStatus.length}, Active: ${usersWithStatus.length - notFoundCount - inactiveCount - errorCount}, Not Found: ${notFoundCount}, Inactive: ${inactiveCount}, Errors: ${errorCount}, Fuzzy Matches: ${fuzzyMatchCount}`);
          
          setUsersWithRole(usersWithStatus);
        } catch (hrError) {
          console.error('Error fetching HR data:', hrError);
          
          // If HR lookup fails, still show users but with unknown HR status
          const usersWithUnknownStatus = usersResponse.map(user => ({
            ...user,
            HR_STATUS: 'Unknown',
            HR_RECORD_FOUND: false,
            HR_LOOKUP_ERROR: 'HR service unavailable'
          }));
          
          setUsersWithRole(usersWithUnknownStatus);
          setMessage({
            text: 'Warning: Unable to verify HR status. Some features may be limited.',
            type: 'warning',
            show: true
          });
        }
      }
    } catch (error) {
      console.error('Error loading users with role:', error);
      setMessage({ 
        text: 'Error loading users. Please try again.', 
        type: 'error', 
        show: true 
      });
    } finally {
      setUserLoading(false);
    }
  };
  
  // Auto-fetch users for roles when app is expanded
  useEffect(() => {
    if (expandedApp) {
      const roles = adminRolesByApp[expandedApp] || [];
      if (roles.length > 0) {
        // Auto-select the first role
        handleViewUsers(roles[0]);
      }
    } else {
      // Clear selection when app is collapsed
      setSelectedAdminRole(null);
      setUsersWithRole([]);
    }
  }, [expandedApp, adminRolesByApp]);

  // Handle closing the alert message
  const handleCloseAlert = () => {
    setMessage({ ...message, show: false });
  };

  // Check if a role is already tagged as admin
  const isRoleAdmin = (roleName) => {
    return adminRoles.some(role => role.ROLE_NAME === roleName && role.APP_NAME === selectedApp);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle app filter change  
  const handleAppFilterChange = (event) => {
    setAppFilter(event.target.value);
  };
  
  // Toggle expanded app in the accordion
  const toggleExpandApp = (appId) => {
    setExpandedApp(expandedApp === appId ? null : appId);
  };

  // Get any non-admin roles and filter by search query
  const getFilteredNonAdminRoles = () => {
    if (!availableRoles.length) return [];
    
    return availableRoles
      .filter(role => !isRoleAdmin(role.name))
      .filter(role => role.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => {
        // Sort by suggested roles first (containing Admin or SuperUser)
        const aIsSuggested = 
          a.name.toLowerCase().includes('admin') || 
          a.name.toLowerCase().includes('superuser') ||
          a.name.toLowerCase().includes('super_user') ||
          a.name.toLowerCase().includes('super-user') ||
          a.name.toLowerCase().includes('super user');
        
        const bIsSuggested = 
          b.name.toLowerCase().includes('admin') || 
          b.name.toLowerCase().includes('superuser') ||
          b.name.toLowerCase().includes('super_user') ||
          b.name.toLowerCase().includes('super-user') ||
          b.name.toLowerCase().includes('super user');
        
        if (aIsSuggested && !bIsSuggested) return -1;
        if (!aIsSuggested && bIsSuggested) return 1;
        return a.name.localeCompare(b.name);
      });
  };

  // Get filtered admin roles by application
  const getFilteredAdminRoles = () => {
    // If there's no app filter, show all apps
    if (!appFilter) return adminRolesByApp;
    
    // Otherwise filter by app name
    const filteredRoles = {};
    
    Object.keys(adminRolesByApp).forEach(appId => {
      const app = apps.find(a => a.id === appId);
      if (app && app.APP_NAME.toLowerCase().includes(appFilter.toLowerCase())) {
        filteredRoles[appId] = adminRolesByApp[appId];
      }
    });
    
    return filteredRoles;
  };

  // Determine if a role is suggested (for highlighting)
  const isRoleSuggested = (roleName) => {
    return (
      roleName.toLowerCase().includes('admin') || 
      roleName.toLowerCase().includes('superuser') ||
      roleName.toLowerCase().includes('super_user') ||
      roleName.toLowerCase().includes('super-user') ||
      roleName.toLowerCase().includes('super user')
    );
  };

  return (
    <mui.Box sx={{ p: 3 }}>
      {/* Alert message */}
      <mui.Snackbar
        open={message.show}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <mui.Alert 
          onClose={handleCloseAlert} 
          severity={message.type} 
          sx={{ width: '100%' }}
        >
          {message.text}
        </mui.Alert>
      </mui.Snackbar>

      {/* Confirmation Dialog for Admin Role Removal */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            width: '450px'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ 
          pb: 1, 
          fontWeight: 600,
          color: theme.palette.error.main
        }}>
          {"Confirm Admin Role Removal"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ 
            color: theme.palette.text.primary,
            mb: 1
          }}>
            Are you sure you want to remove the following admin role?
          </DialogContentText>
          {roleToDelete && (
            <mui.Box sx={{ 
              backgroundColor: alpha(theme.palette.background.default, 0.7), 
              p: 2, 
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              mb: 2
            }}>
              <mui.Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Role: <span style={{ color: theme.palette.primary.main }}>{roleToDelete.ROLE_NAME}</span>
              </mui.Typography>
              <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Application: {apps.find(a => a.id === roleToDelete.APP_ID)?.APP_NAME || apps.find(a => a.id === roleToDelete.APP_NAME)?.APP_NAME || roleToDelete.appName || roleToDelete.APP_NAME}
              </mui.Typography>
            </mui.Box>
          )}
         
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <mui.Button 
            onClick={handleCloseConfirmDialog} 
            variant="outlined"
            sx={{ borderRadius: 1 }}
          >
            Cancel
          </mui.Button>
          <mui.Button 
            onClick={handleRemoveAdminRole} 
            variant="contained" 
            color="error"
            disableElevation
            sx={{ borderRadius: 1 }}
            autoFocus
          >
            Remove Admin Status
          </mui.Button>
        </DialogActions>
      </Dialog>

      {/* Main content */}
      <mui.Grid container spacing={3}>
        {/* Header */}
        <mui.Grid item xs={12}>
          <mui.Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              mb: 3,
              background: 'linear-gradient(135deg, rgba(0,123,255,0.03) 0%, rgba(255,255,255,1) 100%)',
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
              borderRadius: 2
            }}
          >
            <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <mui.Box>
                <mui.Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
              Privileged Access Management
            </mui.Typography>
                <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Monitor and manage administrative access across your organization's applications
            </mui.Typography>
              </mui.Box>
              <mui.Box sx={{ 
                width: 56, 
                height: 56, 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)',
                boxShadow: '0 4px 20px rgba(23, 105, 170, 0.2)'
              }}>
                <ShieldIcon sx={{ color: '#fff', fontSize: 28 }} />
              </mui.Box>
            </mui.Box>
          </mui.Paper>
        </mui.Grid>

        {/* Left Panel: Tag Admin Roles */}
        <mui.Grid item xs={12} md={4}>
          <mui.Paper 
            sx={{ 
              p: 0, 
              height: '100%', 
              overflow: 'hidden', 
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <mui.Box sx={{ 
              p: 2, 
              borderBottom: '1px solid', 
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.05)
            }}>
              <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Tag Administrator Roles
              </mui.Typography>
              <mui.Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 2 }}>
                Select an application and tag roles as administrative
              </mui.Typography>
              
              <mui.FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                <mui.InputLabel id="app-select-label">Application</mui.InputLabel>
                <mui.Select
                  labelId="app-select-label"
                  id="app-select"
                  value={selectedApp || ''}
                  onChange={handleAppChange}
                  label="Application"
                >
                  <mui.MenuItem value="">
                    <em>Select an application</em>
                  </mui.MenuItem>
                  {apps.map(app => (
                    <mui.MenuItem key={app.id} value={app.id}>
                      {app.APP_NAME}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
              
              {selectedApp && (
                <mui.TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <mui.InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </mui.InputAdornment>
                    )
                  }}
                />
              )}
            </mui.Box>

            <mui.Box sx={{ 
              height: 'calc(100vh - 350px)', 
              overflowY: 'auto',
              p: 0
            }}>
              {selectedApp ? (
                getFilteredNonAdminRoles().length > 0 ? (
                  <mui.List dense disablePadding>
                    {getFilteredNonAdminRoles().map(role => (
                      <mui.ListItem 
                        key={role.name}
                        divider
                        secondaryAction={
                          <mui.Tooltip title="Set as Admin Role">
                            <mui.IconButton 
                              edge="end" 
                              aria-label="add admin" 
                              onClick={() => handleAddAdminRole(role.name)}
                              disabled={roleLoading}
                              color="primary"
                              size="small"
                            >
                              <AddIcon fontSize="small" />
                            </mui.IconButton>
                          </mui.Tooltip>
                        }
                        sx={{
                          bgcolor: isRoleSuggested(role.name) ? alpha(theme.palette.warning.light, 0.1) : 'transparent',
                          borderLeft: isRoleSuggested(role.name) ? `3px solid ${theme.palette.warning.main}` : 'none',
                          pl: isRoleSuggested(role.name) ? 2 : 2
                        }}
                      >
                        <mui.ListItemText 
                          primary={role.name} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontWeight: isRoleSuggested(role.name) ? 500 : 400 } 
                          }}
                        />
                      </mui.ListItem>
                    ))}
                  </mui.List>
                ) : (
                  <mui.Box sx={{ p: 3, textAlign: 'center' }}>
                    <mui.Typography variant="body2" color="textSecondary">
                      {searchQuery 
                          ? 'No matching roles found. Try a different search term.' 
                        : 'No non-admin roles available for this application.'}
                    </mui.Typography>
                  </mui.Box>
                )
              ) : (
                <mui.Box sx={{ p: 3, textAlign: 'center' }}>
                  <mui.Typography variant="body2" color="textSecondary">
                    Select an application to manage its admin roles.
                  </mui.Typography>
                </mui.Box>
              )}
            </mui.Box>
          </mui.Paper>
        </mui.Grid>

        {/* Right Panel: Existing Admin Roles and Users */}
        <mui.Grid item xs={12} md={8}>
          <mui.Grid container spacing={2}>
            {/* Admin Roles Panel */}
            <mui.Grid item xs={12}>
              <mui.Paper 
                sx={{ 
                  p: 0, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
              >
                <mui.Box sx={{ 
                  p: 2.5, 
                  borderBottom: '1px solid', 
                  borderColor: alpha(theme.palette.divider, 0.1),
                  background: 'linear-gradient(135deg, rgba(0,123,255,0.03) 0%, rgba(255,255,255,1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShieldIcon 
                      sx={{ 
                        color: theme.palette.primary.main, 
                        mr: 1.5, 
                        fontSize: '1.5rem' 
                      }} 
                    />
                  <mui.Box>
                      <mui.Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      Administrator Roles
                    </mui.Typography>
                    <mui.Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        Current administrator roles across your applications
                    </mui.Typography>
                    </mui.Box>
                  </mui.Box>
                  
                  <mui.TextField
                    size="small"
                    variant="outlined"
                    placeholder="Filter by app name"
                    value={appFilter}
                    onChange={handleAppFilterChange}
                    InputProps={{
                      startAdornment: (
                        <mui.InputAdornment position="start">
                          <FilterListIcon fontSize="small" />
                        </mui.InputAdornment>
                      ),
                      sx: { 
                        fontSize: '0.875rem',
                        borderRadius: 1.5,
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.divider, 0.2)
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.3)
                        }
                      }
                    }}
                    sx={{ width: 200 }}
                  />
                </mui.Box>
                
                <mui.Box sx={{ 
                  maxHeight: 'calc(100vh - 300px)', 
                  overflowY: 'auto',
                  p: loading ? 3 : 0
                }}>
                  {loading ? (
                    <mui.Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <mui.CircularProgress size={24} />
                    </mui.Box>
                  ) : Object.keys(getFilteredAdminRoles()).length > 0 ? (
                    <mui.List dense component="div" disablePadding>
                      {Object.entries(getFilteredAdminRoles()).map(([appId, roles]) => {
                        const app = apps.find(a => a.id === appId);
                        return (
                          <React.Fragment key={appId}>
                            <mui.ListItemButton
                              onClick={() => toggleExpandApp(appId)}
                              sx={{ 
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: expandedApp === appId ? alpha(theme.palette.primary.main, 0.05) : 'transparent'
                              }}
                            >
                              <mui.ListItemIcon>
                                <ShieldIcon 
                                  fontSize="small" 
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              </mui.ListItemIcon>
                              <mui.ListItemText 
                                primary={app ? app.APP_NAME : 'Unknown App'}
                                secondary={`${roles.length} admin role${roles.length === 1 ? '' : 's'}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                              {expandedApp === appId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </mui.ListItemButton>
                            
                            <mui.Collapse in={expandedApp === appId} timeout="auto" unmountOnExit>
                              <mui.Box sx={{ p: 0 }}>
                                {/* Quick Jump Menu for 5+ roles */}
                                {roles.length > 5 && (
                                  <mui.Box sx={{ 
                                    px: 2, 
                                    py: 1.5, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    borderBottom: '1px solid',
                                    borderColor: alpha(theme.palette.divider, 0.2),
                                    backgroundColor: alpha(theme.palette.background.default, 0.6)
                                  }}>
                                    <mui.Typography variant="body2" sx={{ 
                                      fontWeight: 500,
                                      color: theme.palette.text.secondary,
                                      mr: 1.5 
                                    }}>
                                      Quick Jump:
                                    </mui.Typography>
                                    <mui.FormControl size="small" sx={{ minWidth: 200 }}>
                                      <mui.Select
                                        value={selectedAdminRole ? selectedAdminRole.id : ''}
                                        onChange={(e) => {
                                          const role = roles.find(r => r.id === e.target.value);
                                          if (role) handleViewUsers(role);
                                        }}
                                        displayEmpty
                                        variant="outlined"
                                        sx={{ 
                                          height: 32, 
                                          fontSize: '0.8rem',
                                          fontWeight: 500,
                                          '& .MuiSelect-select': { 
                                            py: 0.5,
                                            px: 1.5
                                          }
                                        }}
                                      >
                                        <mui.MenuItem value="" disabled>
                                          <em>Select a role</em>
                                        </mui.MenuItem>
                                        {roles.map(role => (
                                          <mui.MenuItem 
                                            key={role.id} 
                                            value={role.id}
                                            sx={{ 
                                              fontSize: '0.8rem',
                                              py: 1
                                            }}
                                          >
                                            {role.ROLE_NAME}
                                          </mui.MenuItem>
                                        ))}
                                      </mui.Select>
                                    </mui.FormControl>
                                  </mui.Box>
                                )}
                                
                                {/* Role tabs */}
                                <mui.Tabs 
                                  value={selectedAdminRole ? roles.findIndex(r => r.id === selectedAdminRole.id) : 0} 
                                  onChange={(e, newVal) => handleViewUsers(roles[newVal])}
                                  variant="scrollable"
                                  scrollButtons="auto"
                                  sx={{ 
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    minHeight: '48px',
                                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                    '& .MuiTab-root': {
                                      minHeight: '48px',
                                      py: 1.5,
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      color: theme.palette.text.secondary,
                                      transition: 'all 0.2s',
                                      '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                      },
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.03)
                                      }
                                    },
                                    '& .MuiTabs-indicator': {
                                      height: 3,
                                      borderTopLeftRadius: 3,
                                      borderTopRightRadius: 3
                                    }
                                  }}
                                >
                                {roles.map(role => (
                                    <mui.Tab 
                                    key={role.id}
                                      label={role.ROLE_NAME}
                                      icon={
                                        <mui.Tooltip title="Remove Admin Role">
                                          <mui.IconButton 
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              confirmRemoveAdminRole(role);
                                            }}
                                    sx={{
                                              opacity: 0.6,
                                      '&:hover': {
                                                opacity: 1,
                                                backgroundColor: alpha(theme.palette.error.main, 0.1)
                                              }
                                            }}
                                          >
                                            <DeleteIcon 
                                        fontSize="small" 
                                              sx={{ 
                                                color: theme.palette.error.main,
                                                fontSize: '0.875rem'
                                              }} 
                                            />
                                          </mui.IconButton>
                                        </mui.Tooltip>
                                      }
                                      iconPosition="end"
                                    />
                                  ))}
                                </mui.Tabs>
                                
                                {/* Users with role table */}
                                <mui.Box sx={{ p: 2 }}>
                                  {userLoading ? (
                                    <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                      <mui.CircularProgress size={28} sx={{ color: theme.palette.primary.main }} />
                                    </mui.Box>
                                  ) : selectedAdminRole ? (
                                    <mui.Box 
                                    sx={{
                                        borderRadius: 2, 
                                        overflow: 'hidden',
                                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
                                      }}
                                    >
                                      <mui.TableContainer component={mui.Paper} variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                        <mui.Table size="small" aria-label="users with admin role">
                                          <mui.TableHead>
                                            <mui.TableRow sx={{ background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
                                              <mui.TableCell width="35%" sx={{ 
                                                fontWeight: 600, 
                                                color: theme.palette.primary.dark, 
                                                fontSize: '0.75rem', 
                                                letterSpacing: '0.5px',
                                                textTransform: 'uppercase',
                                                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                              }}>User Name</mui.TableCell>
                                              <mui.TableCell width="40%" sx={{ 
                                                fontWeight: 600, 
                                                color: theme.palette.primary.dark, 
                                                fontSize: '0.75rem', 
                                                letterSpacing: '0.5px',
                                                textTransform: 'uppercase',
                                                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                              }}>Email</mui.TableCell>
                                              <mui.TableCell width="25%" sx={{ 
                                                fontWeight: 600, 
                                                color: theme.palette.primary.dark, 
                                                fontSize: '0.75rem', 
                                                letterSpacing: '0.5px',
                                                textTransform: 'uppercase',
                                                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                              }}>HR Status</mui.TableCell>
                                            </mui.TableRow>
                                          </mui.TableHead>
                                          <mui.TableBody>
                                            {usersWithRole.length > 0 ? (
                                              usersWithRole.map((user, index) => (
                                                <mui.TableRow 
                                                  key={user.USER_ID} 
                                                  sx={{ 
                                      '&:hover': {
                                                      backgroundColor: alpha(theme.palette.action.hover, 0.05)
                                                    },
                                                    borderLeft: (() => {
                                                      // Normalize status to handle case insensitivity
                                                      const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                      
                                                      // Only Active status gets green border, everything else gets grey
                                                      if (normalizedStatus === 'ACTIVE') {
                                                        return `3px solid ${theme.palette.success.main}`; // Green for active users
                                                      } else {
                                                        return `3px solid ${theme.palette.grey[300]}`; // Grey for all other statuses
                                                      }
                                                    })()
                                                  }}
                                                >
                                                  <mui.TableCell>
                                                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                      <mui.Avatar 
                                                        sx={{ 
                                                          width: 32, 
                                                          height: 32, 
                                                          bgcolor: (() => {
                                                            // Normalize HR_STATUS to handle case sensitivity issues
                                                            const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                            
                                                            if (normalizedStatus === 'ACTIVE') {
                                                              return alpha(theme.palette.success.main, 0.2); // Green background for active
                                                            } else {
                                                              return alpha(theme.palette.grey[400], 0.1); // Grey for others
                                                            }
                                                          })(),
                                                          color: (() => {
                                                            // Normalize HR_STATUS to handle case sensitivity issues
                                                            const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                            
                                                            if (normalizedStatus === 'ACTIVE') {
                                                              return theme.palette.success.dark; // Dark green text for active
                                                            } else {
                                                              return theme.palette.grey[600]; // Grey for others
                                                            }
                                                          })(),
                                                          border: (() => {
                                                            // Normalize HR_STATUS to handle case sensitivity issues
                                                            const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                            
                                                            if (normalizedStatus === 'ACTIVE') {
                                                              return `1px solid ${alpha(theme.palette.success.main, 0.5)}`; // Green border for active
                                                            } else {
                                                              return 'none'; // No border for others
                                                            }
                                                          })(),
                                                          mr: 1.5,
                                                          fontSize: '0.75rem',
                                                          fontWeight: 600,
                                                          boxShadow: (() => {
                                                            // Normalize HR_STATUS to handle case sensitivity issues
                                                            const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                            
                                                            if (normalizedStatus === 'ACTIVE') {
                                                              return `0 2px 8px ${alpha(theme.palette.success.main, 0.25)}`; // Green shadow for active
                                                            } else {
                                                              return '0 2px 8px rgba(0,0,0,0.05)'; // Default shadow for others
                                                            }
                                                          })()
                                                        }}
                                                      >
                                                        {user.FIRST_NAME.charAt(0)}{user.LAST_NAME.charAt(0)}
                                                      </mui.Avatar>
                                                    </mui.Box>
                                                  </mui.TableCell>
                                                  <mui.TableCell>
                                                    <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                      {user.EMAIL_ADDRESS}
                                                    </mui.Typography>
                                                  </mui.TableCell>
                                                  <mui.TableCell>
                                                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                      {/* Status indicator dot - updated with proper case-insensitive handling */}
                                                      <mui.Box
                                                        sx={{
                                                          width: 8,
                                                          height: 8,
                                                          borderRadius: '50%',
                                                          bgcolor: (() => {
                                                            // Normalize HR_STATUS to handle case sensitivity issues
                                                            const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                            
                                                            // Only Active status gets green indicator, everything else gets grey
                                                            if (normalizedStatus === 'ACTIVE') {
                                                              return theme.palette.success.main; // Green for active
                                                            } else {
                                                              return theme.palette.grey[400]; // Grey for all other statuses
                                                            }
                                                          })(),
                                                          mr: 1,
                                                          boxShadow: (() => {
                                                            // Normalize HR_STATUS to handle case sensitivity issues
                                                            const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                            
                                                            // Only Active status gets a shadow
                                                            if (normalizedStatus === 'ACTIVE') {
                                                              return `0 0 0 2px ${alpha(theme.palette.success.main, 0.2)}`;
                                                            } else {
                                                              return 'none'; // No shadow for all other statuses
                                                            }
                                                          })()
                                                        }}
                                                      />
                                                      {/* Status text and flag rendering with simplified approach */}
                                                      {(() => {
                                                        // Normalize HR_STATUS to handle case sensitivity issues
                                                        const normalizedStatus = user.HR_STATUS ? user.HR_STATUS.toUpperCase() : '';
                                                        
                                                        // Active status - only this gets special treatment
                                                        if (normalizedStatus === 'ACTIVE') {
                                                          return (
                                                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                              <mui.Chip 
                                                                label="Active"
                                                                size="small"
                                                                color="success"
                                                                sx={{ 
                                                                  height: 24, 
                                                                  fontSize: '0.75rem', 
                                                                  fontWeight: 600,
                                                                  backgroundColor: alpha(theme.palette.success.main, 0.15),
                                                                  color: theme.palette.success.dark,
                                                                  '& .MuiChip-label': { px: 1 }
                                                                }}
                                                              />
                                                              {user.HR_FUZZY_MATCH && (
                                                                <mui.Tooltip title={`Fuzzy match with HR record: ${user.HR_MATCHED_EMAIL}`}>
                                                                  <mui.Chip
                                                                    label="Potential Match"
                                                                    size="small"
                                                                    color="info"
                                                                    variant="outlined"
                                                                    sx={{ 
                                                                      ml: 1,
                                                                      height: 20,
                                                                      fontSize: '0.65rem'
                                                                    }}
                                                                  />
                                                                </mui.Tooltip>
                                                              )}
                                                            </mui.Box>
                                                          );
                                                        }
                                                        
                                                        // Everything else is treated as "Not in HR Records" with a flag for removal
                                                        return (
                                                          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <mui.Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                              Not in HR Records
                                                            </mui.Typography>
                                                            <mui.Chip
                                                              icon={<WarningIcon />}
                                                              label="Flag for removal"
                                                              size="small"
                                                              sx={{ 
                                                                ml: 1,
                                                                height: 24,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 600,
                                                                color: theme.palette.warning.dark,
                                                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                                                borderColor: alpha(theme.palette.warning.main, 0.3),
                                                                '& .MuiChip-icon': {
                                                                  color: theme.palette.warning.dark,
                                                                  fontSize: '0.9rem'
                                                                }
                                                              }}
                                                            />
                                                          </mui.Box>
                                                        );
                                                      })()}
                                                    </mui.Box>
                                                  </mui.TableCell>
                                                </mui.TableRow>
                                              ))
                                            ) : (
                                              <mui.TableRow>
                                                <mui.TableCell colSpan={3} align="center">
                                                  <mui.Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                          No users found with this admin role.
                        </mui.Typography>
                                                </mui.TableCell>
                                              </mui.TableRow>
                                            )}
                                          </mui.TableBody>
                                          <mui.TableFooter>
                                            <mui.TableRow>
                                              <mui.TableCell colSpan={3} sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
                                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                  <mui.Chip
                                                    label={`Total users: ${usersWithRole.length}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ height: 24, fontWeight: 500 }}
                                                  />
                                                  {/* Footer with simplified counting - anything not active needs attention */}
                                                  {usersWithRole.filter(u => {
                                                    // Normalize HR_STATUS to handle case sensitivity issues
                                                    const normalizedStatus = u.HR_STATUS ? u.HR_STATUS.toUpperCase() : '';
                                                    return normalizedStatus !== 'ACTIVE';
                                                  }).length > 0 && (
                                                    <mui.Chip
                                                      icon={<WarningIcon />}
                                                      label={`${usersWithRole.filter(u => {
                                                        // Normalize HR_STATUS to handle case sensitivity issues
                                                        const normalizedStatus = u.HR_STATUS ? u.HR_STATUS.toUpperCase() : '';
                                                        return normalizedStatus !== 'ACTIVE';
                                                      }).length} user(s) not in HR records`}
                                                      size="small"
                                                      color="warning"
                                                      sx={{ height: 24, fontSize: '0.7rem', fontWeight: 500 }}
                                                    />
                                                  )}
                      </mui.Box>
                                              </mui.TableCell>
                                            </mui.TableRow>
                                          </mui.TableFooter>
                                        </mui.Table>
                                      </mui.TableContainer>
                                    </mui.Box>
                                  ) : (
                                    <mui.Box 
                                      sx={{ 
                                        p: 4, 
                      textAlign: 'center', 
                                        backgroundColor: alpha(theme.palette.background.default, 0.4),
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                                      }}
                                    >
                                      <mui.Box sx={{ 
                                        width: 56,
                                        height: 56,
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center',
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        margin: '0 auto 16px'
                                      }}>
                                        <PeopleIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                                      </mui.Box>
                                      <mui.Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        Select an admin role to view users
                                      </mui.Typography>
                      <mui.Typography variant="body2" color="textSecondary">
                                        User information will be displayed here
                                      </mui.Typography>
                                    </mui.Box>
                                  )}
                                </mui.Box>
                              </mui.Box>
                            </mui.Collapse>
                          </React.Fragment>
                        );
                      })}
                    </mui.List>
                  ) : (
                    <mui.Box sx={{ p: 3, textAlign: 'center' }}>
                      <mui.Typography variant="body2" color="textSecondary">
                        {appFilter 
                          ? 'No matching applications found. Try a different filter.' 
                          : 'No administrator roles have been configured.'}
                      </mui.Typography>
                    </mui.Box>
                  )}
                </mui.Box>
              </mui.Paper>
            </mui.Grid>
          </mui.Grid>
        </mui.Grid>
      </mui.Grid>
    </mui.Box>
  );
};

const SysOwnerCompliance = () => {
    // Create a memoized logger function to reduce excessive logging
    const logger = React.useMemo(() => {
        const logged = new Set();
        return {
            error: (key, ...args) => {
                const logKey = key + JSON.stringify(args.map(a => typeof a === 'object' ? 'obj' : a));
                if (!logged.has(logKey)) {
                    logged.add(logKey);
                    console.error(...args);
                }
            }
        };
    }, []);

    // Handler functions for date filters
    const handleProvisioningDateRangeChange = (type, newDate) => {
        // Validate the date
        if (newDate instanceof Date && !isNaN(newDate)) {
            setProvisioningDateRange(prev => ({
                ...prev,
                [type]: newDate
            }));
        } else {
            // If invalid date, don't update state and optionally show an error
            console.error("Invalid date provided:", newDate);
        }
    };

    const handleTerminationDateRangeChange = (type, newDate) => {
        // Validate the date
        if (newDate instanceof Date && !isNaN(newDate)) {
            setTerminationDateRange(prev => ({
                ...prev,
                [type]: newDate
            }));
        } else {
            // If invalid date, don't update state and optionally show an error
            console.error("Invalid date provided:", newDate);
        }
    };

    const applyProvisioningDateFilter = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const applyTerminationDateFilter = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    // Create theme inside the component
    const theme = createTheme({
        palette: {
            primary: {
                main: '#0288d1', // Light Blue from PolicyLibrary
                light: '#5eb8ff',
                dark: '#005b9f',
                contrastText: '#fff',
            },
            secondary: {
                main: '#4caf50', // Green from PolicyLibrary
                light: '#80e27e',
                dark: '#087f23',
                contrastText: '#fff',
            },
            error: {
                main: '#f44336', // Red
                light: '#e57373',
                dark: '#d32f2f',
            },
            warning: {
                main: '#fb8c00', // Orange for "Needs Review"
                light: '#ffb74d',
                dark: '#e65100',
            },
            info: {
                main: '#757575', // Grey for "Incomplete Setup"
                light: '#a4a4a4',
                dark: '#494949',
            },
            success: {
                main: '#2e7d32', // Green for "Compliant"
                light: '#66bb6a',
                dark: '#1b5e20',
            },
            background: {
                default: '#f8fafc',
                paper: '#ffffff',
            },
            text: {
                primary: '#2c3e50',
                secondary: '#7f8c8d',
            },
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h4: {
                fontWeight: 600,
                fontSize: '1.75rem',
            },
            h5: {
                fontWeight: 600,
                fontSize: '1.25rem',
            },
            h6: {
                fontWeight: 600,
                fontSize: '1rem',
            },
            subtitle1: {
                fontWeight: 500,
                fontSize: '0.95rem',
            },
            subtitle2: {
                fontWeight: 500,
                fontSize: '0.85rem',
            },
            body1: {
                fontSize: '0.875rem',
            },
            body2: {
                fontSize: '0.8rem',
            },
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        textTransform: 'none',
                        boxShadow: 'none',
                        fontWeight: 500,
                        '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        },
                    },
                    containedPrimary: {
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        fontSize: '0.75rem',
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    h4: {
                        lineHeight: 1.3,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        minWidth: 100,
                        transition: 'all 0.2s',
                        borderBottom: '2px solid transparent',
                        '&.Mui-selected': {
                            color: '#0288d1',
                            fontWeight: 600,
                        },
                    },
                },
            },
        },
    });

    const [apps, setApps] = useState([]);
    const [appsProvisioning, setAppsProvisioning] = useState([]);
    const [appsTermination, setAppsTermination] = useState([]);
    const [incompleteSetupAuth, setIncompleteSetupAuth] = useState([]);
    const [withExceptions, setWithExceptions] = useState([]);
    const [withProvExceptions, setWithProvExceptions] = useState([]);
    const [authCount, setAuthCount] = useState([]);
    const [authPolicyViolationCount, setAuthPolicyViolationCount] = useState([]);
    const [compliantCount, setCompliantCount] = useState([]);
    const [authIncompleteCount, setAuthIncompleteCount] = useState([]);
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [openProvisioningModal, setOpenProvisioningModal] = useState(false);
    const [openTerminationModal, setOpenTerminationModal] = useState(false);
    const [passwordRows, setPasswordRows] = useState([]);
    // PROVISIONING
    const [provisioningRowDetails, setProvisioningRowsDetails] = useState([]);
    const [provisioningMissingDocsRowDetails, setProvisioningMissingDocsRowsDetails] = useState([]);
    const [provisioningLateApprovalRowDetails, setProvisioningLateApprovalRowsDetails] = useState([]);
    // TERMINATION
    const [terminationRowDetails, setTerminationRowsDetails] = useState([]);
    const [terminationLateRemovalDetails, setTerminationLateRemovalRowsDetails] = useState([]);
    // PROVISIONING DONUTS
    const [provNoDocCount, setProvNoDocCount] = useState([]);
    const [provLateCount, setProvLateCount] = useState([]);
    // TERMINATION DONUT
    const [lateremovalCount, setLateRemovalCount] = useState([]);
    // Added state for UI enhancements
    const [isLoading, setIsLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');
    const [showFilters, setShowFilters] = useState(false);
    const [filteredRows, setFilteredRows] = useState({
        auth: [],
        provisioning: [],
        termination: []
    });
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [lastRefreshDate, setLastRefreshDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('1'); // Add state for active tab
    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState([]);
    const [compliancePieData, setCompliancePieData] = useState([]);
    const [provisioningPieData, setProvisioningPieData] = useState([]);
    const [terminationPieData, setTerminationPieData] = useState([]);
    const [showChartSelector, setShowChartSelector] = useState(false);
    const [provisioningDateRange, setProvisioningDateRange] = useState({
        startDate: new Date(new Date().getFullYear(), 0, 1), // January 1st of current year
        endDate: new Date(new Date().getFullYear(), 11, 31)  // December 31st of current year
    });
    const [terminationDateRange, setTerminationDateRange] = useState({
        startDate: new Date(new Date().getFullYear(), 0, 1), // January 1st of current year
        endDate: new Date(new Date().getFullYear(), 11, 31)  // December 31st of current year
    });

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const currentUser = await userService.fetchCurrentUser()
                if (currentUser) {
                    const assignedApps = await appService.fetchAppsByOwner(currentUser.id)

                    if (assignedApps) {
                        setApps(assignedApps);

                        // GET ALL THE PASSWORD DATA
                        const passwordPolicyPromises = assignedApps.map(async (app) => {
                            if (app.id) {
                                try {
                                    return { id: app.id, policy: await appPasswordService.fetchAppPasswordByApp(app.id) };
                                } catch (err) {
                                    logger.error(`policy-${app.id}`, 'Error fetching policy for company ID', app.COMPANY_NAME, ':', err);
                                    return { id: app.id, policy: null };
                                }
                            } else {
                                return { id: app.id, policy: null };
                            }
                        });

                        const passwordPolicies = await Promise.all(passwordPolicyPromises);

                        if (passwordPolicies) {
                            const combinedDataMap = passwordPolicies.reduce((acc, { id, policy }) => {
                                acc[id] = {
                                    passwordPolicy: policy ? policy.pw_policy : null,
                                    passwordConfigured: policy ? policy.data : null,
                                    complianceStatus: policy ? policy.compliance_status : null,
                                };
                                return acc;
                            }, {});

                            // Enhance assignedApps with combined data
                            const appsWithCombinedData = assignedApps.map((app) => ({
                                ...app,
                                passwordPolicy: combinedDataMap[app.id]?.passwordPolicy || null,
                                passwordConfigured: combinedDataMap[app.id]?.passwordConfigured || null,
                                complianceStatus: combinedDataMap[app.id]?.complianceStatus || null
                            }));

                            // Set the combined data in state
                            setApps(appsWithCombinedData);

                            const statusValues = appsWithCombinedData.map(app => app.complianceStatus?.status).map(status => status === undefined ? 'undefined' : status);

                            const statusCounts = statusValues.reduce((counts, status) => {
                                counts[status] = (counts[status] || 0) + 1;
                                return counts;
                            }, {});

                            const formattedStatusCounts = Object.keys(statusCounts).map(status => ({
                                label: status === 'undefined' ? 'Incomplete Setup' : status,
                                value: statusCounts[status]
                            }));

                            setIncompleteSetupAuth(formattedStatusCounts);
                            setWithExceptions(formattedStatusCounts);

                            const statusesToCount = ['Needs Review', 'Incomplete Setup'];
                            const policyViolationTocount = ['Needs Review'];
                            const incompleteTocount = ['Incomplete Setup'];
                            const compliantTocount = ['Compliant'];

                            const compliantApp = formattedStatusCounts
                                .filter(item => compliantTocount.includes(item.label))
                                .reduce((total, item) => total + item.value, 0);

                            setCompliantCount(compliantApp);

                            const policyViolation = formattedStatusCounts
                                .filter(item => policyViolationTocount.includes(item.label))
                                .reduce((total, item) => total + item.value, 0);

                            setAuthPolicyViolationCount(policyViolation);

                            const incompleteSetup = formattedStatusCounts
                                .filter(item => incompleteTocount.includes(item.label))
                                .reduce((total, item) => total + item.value, 0);

                            setAuthIncompleteCount(incompleteSetup);

                            const authcount = formattedStatusCounts
                                .filter(item => statusesToCount.includes(item.label))
                                .reduce((total, item) => total + item.value, 0);

                            setAuthCount(authcount);
                            
                            // Prepare pie chart data in a specific order
                            setCompliancePieData([
                                {id: 'Compliant', value: compliantApp, label: 'Compliant'},
                                {id: 'Needs Review', value: policyViolation, label: 'Needs Review'},
                                {id: 'Incomplete Setup', value: incompleteSetup, label: 'Incomplete Setup'}
                            ]);
                        }

                        // GET ALL PROVISIONING DATA
                        const provisioningDataPromises = assignedApps
                            .filter(app => app.id)
                            .map(async (app) => {
                                try {
                                    // Format dates for API
                                    const startDateFormatted = formatDateForAPI(provisioningDateRange.startDate);
                                    const endDateFormatted = formatDateForAPI(provisioningDateRange.endDate);
                                    
                                    // Pass date range parameters to API
                                    const data = await appService.fetchAppsRecordByIdAndGrantDate(
                                        app.id, 
                                        startDateFormatted, 
                                        endDateFormatted
                                    );
                                    return { id: app.id, data: data };
                                } catch (err) {
                                    logger.error(`prov-${app.id}`, 'Error fetching application record for', app.APP_NAME, ':', err);
                                    return { id: app.id, data: null };
                                }
                            });

                        const provisioningData = await Promise.all(provisioningDataPromises);

                        if (provisioningData && provisioningData.length > 0) {
                            const combinedProvisioningDataMap = provisioningData.reduce((acc, { id, data }) => {
                                acc[id] = {
                                    data: data ? data : null,
                                };
                                return acc;
                            }, {});

                            // Enhance assignedApps with combined data
                            const appsWithCombinedProvisioningData = assignedApps.map((app) => {
                                const appData = combinedProvisioningDataMap[app.id]?.data || null;
                                return {
                                    ...app,
                                    data: appData,
                                };
                            });

                            setAppsProvisioning(appsWithCombinedProvisioningData);
                        } else {
                            logger.error('no-prov-data', 'No provisioning data fetched');
                        }

                        // GET TERMINATION DATA
                        const terminationDataPromises = assignedApps
                            .filter(app => app.id)
                            .map(async (app) => {
                                try {
                                    // Format dates for API
                                    const startDateFormatted = formatDateForAPI(terminationDateRange.startDate);
                                    const endDateFormatted = formatDateForAPI(terminationDateRange.endDate);
                                    
                                    // Pass date range parameters to API
                                    const data = await appService.fetchAppsRecordByIdAndRemoveDate(
                                        app.id, 
                                        startDateFormatted, 
                                        endDateFormatted
                                    );
                                    return { id: app.id, data: data };
                                } catch (err) {
                                    logger.error(`term-${app.id}`, 'Error fetching application record for', app.APP_NAME, ':', err);
                                    return { id: app.id, data: null };
                                }
                            });

                        const terminationData = await Promise.all(terminationDataPromises);

                        if (terminationData.length > 0) {
                            const combinedTerminationDataMap = terminationData.reduce((acc, { id, data }) => {
                                acc[id] = {
                                    data: data ? data : null,
                                };
                                return acc;
                            }, {});

                            // Enhance assignedApps with combined data
                            const appsWithCombinedTerminationData = assignedApps.map((app) => ({
                                ...app,
                                data: combinedTerminationDataMap[app.id]?.data || null,
                            }));

                            setAppsTermination(appsWithCombinedTerminationData);
                        } else {
                            logger.error('no-term-data', 'No termination data fetched');
                        }

                        // Update last refresh time
                        setLastRefreshDate(new Date());
                    }
                }
            } catch (error) {
                logger.error('fetch-data', 'Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [refreshTrigger]);

    // Add new useEffect to load the active tab from localStorage on component mount
    useEffect(() => {
        const savedTab = localStorage.getItem('complianceActiveTab');
        if (savedTab) {
            setActiveTab(savedTab);
        }
    }, []);

    // Add function to handle tab changes that also updates localStorage
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        localStorage.setItem('complianceActiveTab', newValue);
    };

    useEffect(() => {
        let totalUniqueApprovalCount = 0;
        let totalLateApprovalCount = 0;
        let totalLateRemovalCount = 0;

        if (appsProvisioning && appsProvisioning.length > 0) {
            appsProvisioning.forEach(app => {
                if (!app.data || !app.data.new_users_per_app) return;
                
                const dataLength = app.data.new_users_per_app.length;

                const uniqueApproval = app.data.with_approval.filter((value, index, self) => {
                    return index === self.findIndex((t) => (
                        t.email === value.email && t.role === value.role
                    ));
                });

                const lateApproval = app.data.late_approval.filter((value, index, self) => {
                    return index === self.findIndex((t) => (
                        t.email === value.email && t.role === value.role
                    ));
                });

                const uniqueApprovalCount = uniqueApproval.length;
                const noApproval = dataLength - uniqueApprovalCount;
                const lateApprovalCount = lateApproval.length;

                if (noApproval > 0) {
                    totalUniqueApprovalCount += 1;
                }

                if (lateApprovalCount > 0) {
                    totalLateApprovalCount += 1;
                }
            });

            setProvNoDocCount(totalUniqueApprovalCount);
            setProvLateCount(totalLateApprovalCount);

            // Set provisioning pie data
            setProvisioningPieData([
                { id: 'Compliant', value: appsProvisioning.length - (totalUniqueApprovalCount + totalLateApprovalCount), label: 'Compliant' },
                { id: 'No Approval', value: totalUniqueApprovalCount, label: 'No Approval' },
                { id: 'Late Approval', value: totalLateApprovalCount, label: 'Late Approval' }
            ]);
        }

        // Termination
        if (appsTermination && appsTermination.length > 0) {
            appsTermination.forEach(app => {
                if (!app.data || !app.data.late_removal) return;

                const lateRemoval = app.data.late_removal.filter((value, index, self) => {
                    return index === self.findIndex((t) => (
                        t.email === value.email && t.role === value.role
                    ));
                });

                const lateRemovalCount = lateRemoval.length;

                if (lateRemovalCount > 0) {
                    totalLateRemovalCount += 1;
                }
            });

            setLateRemovalCount(totalLateRemovalCount);

            // Set termination pie data
            setTerminationPieData([
                { id: 'Compliant', value: appsTermination.length - totalLateRemovalCount, label: 'Compliant' },
                { id: 'Late Removal', value: totalLateRemovalCount, label: 'Late Removal' }
            ]);
        }
    }, [appsProvisioning, appsTermination]);

    // Format date helper
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return format(new Date(date), 'MMMM dd, yyyy');
    };

    // Handle search input changes
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle status filter changes
    const handleStatusFilterChange = (status) => {
        setSelectedStatusFilter(status);
    };

    // Toggle filters visibility
    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Reset filters
    const resetFilters = () => {
        setSelectedStatusFilter('All');
        setSearchTerm('');
    };

    // Handle category changes
    const handleCategoryChange = (category) => {
        setActiveCategory(category);
    };

    const allException = apps.map(status => ({
        label: status.complianceStatus,
        value: 0
    }));

    // Build a refreshed rows object with better formatting
    const rows = apps.map((app, index) => {
        const nonCompliantFields = app.complianceStatus?.non_compliant_fields;
        const nonCompliantCount = Array.isArray(nonCompliantFields) ? nonCompliantFields.length : 0;
        const status = (app.complianceStatus && 'status' in app.complianceStatus && app.complianceStatus.status) 
            ? app.complianceStatus.status 
            : 'Needs Review';

        // Apply severity level based on compliance status
        let severity = 'info';
        if (status === 'Compliant') {
            severity = 'success'; // Green
        } else if (status === 'Needs Review') {
            severity = 'warning'; // Orange
        } else if (status === 'Incomplete Setup') {
            severity = 'info'; // Grey (using info theme color)
        }

        return {
            id: index + 1,
            APP_NAME: app.APP_NAME || '-',
            COMPANY_NAME: app.COMPANY_NAME || '-',
            AUTHENTICATION_TYPE: app.AUTHENTICATION_TYPE || '-',
            appID: app.id,
            geninfo: app.SETUP_GENINFO,
            userdata: app.SETUP_USERDATA,
            process: app.SETUP_PROCESS,
            complianceStatus: status,
            severity: severity,
            nonCompliantFields: Array.isArray(nonCompliantFields) ? nonCompliantFields.join(', ') : 'INCOMPLETE SETUP',
            nonCompliantCount: nonCompliantCount,
            passwordPolicy: app.passwordPolicy ? app.passwordPolicy : '',
            passwordConfigured: app.passwordConfigured ? app.passwordConfigured : '',
            needsAction: status !== 'Compliant'
        };
    });

    // Build provisioning rows with better formatting
    const provisioningrows = appsProvisioning.map((app, index) => {
        if (!app.data || !app.data.new_users_per_app) {
            return {
                id: index + 1,
                APP_NAME: app.APP_NAME || '-',
                COMPANY_NAME: app.COMPANY_NAME || '-',
                TOTALGRANTED: 0,
                NOAPPROVAL: 0,
                LATEAPPROVAL: 0,
                COMPLIANCESTATUS: 'No Data',
                severity: 'info',
                appID: app.id,
                needsAction: false
            };
        }

        const dataLength = Array.isArray(app.data.new_users_per_app) ? app.data.new_users_per_app.length : 0;

        const withApprovalArray = Array.isArray(app.data.with_approval) ? app.data.with_approval : [];
        const withoutApprovalArray = Array.isArray(app.data.without_approval) ? app.data.without_approval : [];
        const lateApprovalArray = Array.isArray(app.data.late_approval) ? app.data.late_approval : [];

        const uniqueApproval = withApprovalArray.filter((value, index, self) => {
            return index === self.findIndex((t) => (
                (t.EMAIL_ADDRESS === value.EMAIL_ADDRESS || t.email === value.email) && 
                (t.ROLE_NAME === value.ROLE_NAME || t.role === value.role)
            ));
        });

        const lateApproval = lateApprovalArray.filter((value, index, self) => {
            return index === self.findIndex((t) => (
                (t.EMAIL_ADDRESS === value.EMAIL_ADDRESS || t.email === value.email) && 
                (t.ROLE_NAME === value.ROLE_NAME || t.role === value.role)
            ));
        });

        const uniqueApprovalCount = uniqueApproval.length;
        const noApproval = withoutApprovalArray.length;
        const lateApprovalCount = lateApproval.length;
        const exceptionCount = noApproval + lateApprovalCount;

        let complianceStatus = 'Compliant';
        let severity = 'success';
        let needsAction = false;

        // If there are no records at all, mark as "No Data" instead of "Compliant"
        if (dataLength === 0) {
            complianceStatus = 'No Data';
            severity = 'info';
        } else if (exceptionCount > 0) {
            complianceStatus = "Needs Review";
            severity = 'warning';
            needsAction = true;
        }

        return {
            id: index + 1,
            APP_NAME: app.APP_NAME || '-',
            COMPANY_NAME: app.COMPANY_NAME || '-',
            TOTALGRANTED: dataLength || '-',
            NOAPPROVAL: noApproval || '-',
            LATEAPPROVAL: lateApprovalCount || '-',
            COMPLIANCESTATUS: complianceStatus || '-',
            severity: severity,
            appID: app.id,
            needsAction: needsAction
        };
    });

    // Build termination rows with better formatting
    const terminationrows = appsTermination.map((app, index) => {
        if (!app.data || !app.data.removal_per_app) {
            return {
                id: index + 1,
                APP_NAME: app.APP_NAME || '-',
                COMPANY_NAME: app.COMPANY_NAME || '-',
                TOTALTERMINATED: 0,
                LATEREMOVAL: 0,
                COMPLIANCESTATUS: 'No Data',
                severity: 'info',
                appID: app.id,
                needsAction: false
            };
        }

        const dataLength = app.data.removal_per_app.length;

        const uniqueremoval = app.data.removal_per_app.filter((value, index, self) => {
            return index === self.findIndex((t) => (
                t.email === value.email && t.role === value.role
            ));
        });

        const lateremoval = app.data.late_removal.filter((value, index, self) => {
            return index === self.findIndex((t) => (
                t.email === value.email && t.role === value.role
            ));
        });

        const uniqueremovalcount = uniqueremoval.length;
        const lateremovalcount = lateremoval.length;

        let complianceStatus = 'Compliant';
        let severity = 'success';
        let needsAction = false;

        // If there are no records at all, mark as "No Data" instead of "Compliant"
        if (dataLength === 0) {
            complianceStatus = 'No Data';
            severity = 'info';
        } else if (lateremovalcount > 0) {
            complianceStatus = "Needs Review";
            severity = 'warning';
            needsAction = true;
        }

        return {
            id: index + 1,
            APP_NAME: app.APP_NAME || '-',
            COMPANY_NAME: app.COMPANY_NAME || '-',
            TOTALTERMINATED: dataLength || '-',
            LATEREMOVAL: lateremovalcount || '-',
            COMPLIANCESTATUS: complianceStatus || '-',
            severity: severity,
            appID: app.id,
            needsAction: needsAction
        };
    });

    // Filter rows based on selected status filter
    const filteredAuthRows = React.useMemo(() => {
        if (selectedStatusFilter === 'All') {
            return rows;
        } else {
            return rows.filter(row => row.complianceStatus === selectedStatusFilter);
        }
    }, [rows, selectedStatusFilter]);

    // Filter provisioning rows based on selected status filter
    const filteredProvisioningRows = React.useMemo(() => {
        if (selectedStatusFilter === 'All') {
            return provisioningrows;
        } else {
            return provisioningrows.filter(row => row.COMPLIANCESTATUS === selectedStatusFilter);
        }
    }, [provisioningrows, selectedStatusFilter]);

    // Filter termination rows based on selected status filter
    const filteredTerminationRows = React.useMemo(() => {
        if (selectedStatusFilter === 'All') {
            return terminationrows;
        } else {
            return terminationrows.filter(row => row.COMPLIANCESTATUS === selectedStatusFilter);
        }
    }, [terminationrows, selectedStatusFilter]);

    // Search filter logic
    React.useEffect(() => {
        const applySearchFilter = () => {
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                
                const searchedAuth = filteredAuthRows.filter(row => 
                    Object.values(row).some(value => 
                        value && typeof value === 'string' && value.toLowerCase().includes(searchLower)
                    )
                );
                
                const searchedProvisioning = filteredProvisioningRows.filter(row => 
                    Object.values(row).some(value => 
                        value && typeof value === 'string' && value.toLowerCase().includes(searchLower)
                    )
                );
                
                const searchedTermination = filteredTerminationRows.filter(row => 
                    Object.values(row).some(value => 
                        value && typeof value === 'string' && value.toLowerCase().includes(searchLower)
                    )
                );
                
                setFilteredRows({
                    auth: searchedAuth,
                    provisioning: searchedProvisioning,
                    termination: searchedTermination
                });
            } else {
                setFilteredRows({
                    auth: filteredAuthRows,
                    provisioning: filteredProvisioningRows,
                    termination: filteredTerminationRows
                });
            }
        };

        // Use a timeout to avoid constant re-renders
        const timer = setTimeout(applySearchFilter, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, filteredAuthRows, filteredProvisioningRows, filteredTerminationRows]);

    // Updated columns with better formatting and icons
    const columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'COMPANY_NAME', headerName: 'Company', flex: 1 },
        { field: 'AUTHENTICATION_TYPE', headerName: 'Authentication Type', sortable: false, flex: 1 },
        { 
            field: 'complianceStatus', 
            headerName: 'Compliance Status', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                <StatusChip
                    label={params.value}
                    status={params.value}
                    icon={
                        params.value === 'Compliant' ? <VerifiedIcon fontSize="small" /> :
                        params.value === 'Needs Review' ? <ErrorOutlineIcon fontSize="small" /> :
                        params.value === 'No Data' ? <InfoIcon fontSize="small" /> :
                        <WarningAmberIcon fontSize="small" />
                    }
                    size="small"
                />
            )
        },
        { 
            field: 'nonCompliantCount', 
            headerName: 'Violations', 
            sortable: true, 
            width: 120,
            renderCell: (params) => (
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {params.value > 0 ? (
                        <mui.Chip
                            label={params.value}
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                color: theme.palette.error.main,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: '12px',
                                height: '24px',
                                '.MuiChip-label': {
                                    px: 1
                                }
                            }}
                        />
                    ) : (
                        <mui.Chip
                            label="None"
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                color: theme.palette.success.main,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: '12px',
                                height: '24px',
                                '.MuiChip-label': {
                                    px: 1
                                }
                            }}
                        />
                    )}
                </mui.Box>
            )
        },
        { 
            field: 'nonCompliantFields', 
            headerName: 'Field Names', 
            sortable: false, 
            flex: 1,
            renderCell: (params) => (
                <mui.Tooltip title={params.value} arrow>
                    <mui.Typography 
                        variant="body2" 
                        sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%'
                        }}
                    >
                        {params.value}
                    </mui.Typography>
                </mui.Tooltip>
            )
        },
    ];

    const provisioningcolumns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'COMPANY_NAME', headerName: 'Company', flex: 1 },
        { 
            field: 'TOTALGRANTED', 
            headerName: 'New Roles/Users', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                <mui.Typography
                    variant="body2"
                    sx={{
                        fontWeight: params.value > 0 ? 'bold' : 'normal',
                        color: params.value > 0 ? theme.palette.primary.main : theme.palette.text.secondary
                    }}
                >
                    {params.value}
                </mui.Typography>
            )
        },
        { 
            field: 'NOAPPROVAL', 
            headerName: 'No Approval', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                params.value > 0 ? (
                    <mui.Chip
                        label={params.value}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: theme.palette.warning.main,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: '12px',
                            height: '24px',
                            '.MuiChip-label': {
                                px: 1
                            }
                        }}
                    />
                ) : (
                    <mui.Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem'
                        }}
                    >
                        {params.value}
                    </mui.Typography>
                )
            )
        },
        { 
            field: 'LATEAPPROVAL', 
            headerName: 'Late Approval', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                params.value > 0 ? (
                    <mui.Chip
                        label={params.value}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: theme.palette.warning.main,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: '12px',
                            height: '24px',
                            '.MuiChip-label': {
                                px: 1
                            }
                        }}
                    />
                ) : (
                    <mui.Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem'
                        }}
                    >
                        {params.value}
                    </mui.Typography>
                )
            )
        },
        { 
            field: 'COMPLIANCESTATUS', 
            headerName: 'Compliance Status', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                <StatusChip
                    label={params.value}
                    status={params.value}
                    icon={
                        params.value === 'Compliant' ? <VerifiedIcon fontSize="small" /> :
                        params.value === 'Needs Review' ? <ErrorOutlineIcon fontSize="small" /> :
                        params.value === 'No Data' ? <InfoIcon fontSize="small" /> :
                        <WarningAmberIcon fontSize="small" />
                    }
                    size="small"
                />
            )
        },
    ];

    const terminationcolumns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'COMPANY_NAME', headerName: 'Company', flex: 1 },
        { 
            field: 'TOTALTERMINATED', 
            headerName: 'Termed Users', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                <mui.Typography
                    variant="body2"
                    sx={{
                        fontWeight: params.value > 0 ? 'bold' : 'normal',
                        color: params.value > 0 ? theme.palette.primary.main : theme.palette.text.secondary
                    }}
                >
                    {params.value}
                </mui.Typography>
            )
        },
        { 
            field: 'LATEREMOVAL', 
            headerName: 'Late Removal', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                params.value > 0 ? (
                    <mui.Chip
                        label={params.value}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: theme.palette.warning.main,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: '12px',
                            height: '24px',
                            '.MuiChip-label': {
                                px: 1
                            }
                        }}
                    />
                ) : (
                    <mui.Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem'
                        }}
                    >
                        {params.value}
                    </mui.Typography>
                )
            )
        },
        { 
            field: 'COMPLIANCESTATUS', 
            headerName: 'Compliance Status', 
            sortable: true, 
            flex: 1,
            renderCell: (params) => (
                <StatusChip
                    label={params.value}
                    status={params.value}
                    icon={
                        params.value === 'Compliant' ? <VerifiedIcon fontSize="small" /> :
                        params.value === 'Needs Review' ? <ErrorOutlineIcon fontSize="small" /> :
                        params.value === 'No Data' ? <InfoIcon fontSize="small" /> :
                        <WarningAmberIcon fontSize="small" />
                    }
                    size="small"
                />
            )
        },
    ];

    //DATA FOR PROVISIONING TABLE DETAILS

    const mapPasswordPolicyToRows = (configure, policy) => [
        createData('Password Length', policy.LENGTH, configure.LENGTH, configure.LENGTH >= policy.LENGTH ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Password Age', policy.AGE, configure.AGE, configure.AGE <= policy.AGE ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Password History', policy.HISTORY, configure.HISTORY, configure.HISTORY >= policy.HISTORY ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Account Lockout Attempts', policy.LOCKOUT_ATTEMPT, configure.LOCKOUT_ATTEMPT, configure.LOCKOUT_ATTEMPT <= policy.LOCKOUT_ATTEMPT ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Require Special Character', policy.SPECIAL_CHAR ? 'Yes' : 'No', configure.SPECIAL_CHAR ? 'Yes' : 'No', configure.SPECIAL_CHAR === policy.SPECIAL_CHAR ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Require Upper Case', policy.UPPER ? 'Yes' : 'No', configure.UPPER ? 'Yes' : 'No', configure.UPPER === policy.UPPER ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Require Lower Case', policy.LOWER ? 'Yes' : 'No', configure.LOWER ? 'Yes' : 'No', configure.LOWER === policy.LOWER ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
        createData('Require Number', policy.NUMBER ? 'Yes' : 'No', configure.NUMBER ? 'Yes' : 'No', configure.NUMBER === policy.NUMBER ? 
            <mui.Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VerifiedIcon sx={{ fontSize: '1rem' }} />
            </mui.Box> : 
            <mui.Box sx={{ color: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
            </mui.Box>),
    ];

    const handleClick = (event, app) => {
        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
        setSelectedApp(app);

        const matchingPolicy = app.passwordPolicy
        const matchingConfiguration = app.passwordConfigured

        if (matchingPolicy || matchingConfiguration) {

            const policyData = matchingPolicy || {};
            const configData = matchingConfiguration || {};
            const passwordRows = mapPasswordPolicyToRows(configData, policyData);
            setPasswordRows(passwordRows);

        } else {

            setPasswordRows([]);
        }

        setOpenPasswordModal(true)
    };

    const provisioningColumnsDetails = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'DATE_GRANTED', headerName: 'Date Granted', flex: 1 },

    ];

    const provisioningLateApprovalColumnsDetails = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'DATE_APPROVED', headerName: 'Date Approved', flex: 1 },
        { field: 'DATE_GRANTED', headerName: 'Date Granted', flex: 1 },
    ];

    const terminationColumnsDetails = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'DATE_REVOKED', headerName: 'Date Removed', flex: 1 },

    ];

    const terminationLateRemovalColumnsDetails = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'APP_NAME', headerName: 'Application Name', flex: 1 },
        { field: 'FIRST_NAME', headerName: 'First Name', flex: 1 },
        { field: 'LAST_NAME', headerName: 'Last Name', flex: 1 },
        { field: 'ROLE_NAME', headerName: 'Role', flex: 1 },
        { field: 'HR_TERMED_DATE', headerName: 'HR Termed Date', flex: 1 },
        { field: 'SYSTEM_TERMED_DATE', headerName: 'System Removal Date', flex: 1 },
    ];

    const handleProvisioningClick = (event, app) => {
        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
        setSelectedApp(app);

        // Filter the appsProvisioning array based on the selected app
        const filteredApps = appsProvisioning.filter(provisioningApp => provisioningApp.id === app.appID);

        if (filteredApps.length === 0 || !filteredApps[0].data) {
            setProvisioningRowsDetails([]);
            setProvisioningMissingDocsRowsDetails([]);
            setProvisioningLateApprovalRowsDetails([]);
            setOpenProvisioningModal(true);
            return;
        }

        const provApp = filteredApps[0];
        
        // Ensure we have valid arrays to work with
        const newUsersData = Array.isArray(provApp.data.new_users_per_app) ? provApp.data.new_users_per_app : [];
        const withoutApprovalData = Array.isArray(provApp.data.without_approval) ? provApp.data.without_approval : [];
        const lateApprovalData = Array.isArray(provApp.data.late_approval) ? provApp.data.late_approval : [];
        
        // All new users
        const allNewUsers = newUsersData.map((user, index) => {
            return {
                id: index + 1,
                APP_NAME: provApp.APP_NAME,
                FIRST_NAME: user.FIRST_NAME || "",
                LAST_NAME: user.LAST_NAME || "",
                EMAIL_ADDRESS: user.EMAIL_ADDRESS || "",
                ROLE_NAME: user.ROLE_NAME || "",
                DATE_GRANTED: user.DATE_GRANTED || "",
            };
        });
        
        // Missing documents/No approval
        const missingDocuments = withoutApprovalData.map((user, index) => {
            return {
                id: index + 1,
                APP_NAME: provApp.APP_NAME,
                FIRST_NAME: user.FIRST_NAME || "",
                LAST_NAME: user.LAST_NAME || "",
                EMAIL_ADDRESS: user.EMAIL_ADDRESS || user.email || "",
                ROLE_NAME: user.ROLE_NAME || user.role || "",
                DATE_GRANTED: user.DATE_GRANTED || user.date_granted || "",
            };
        });
        
        // Late approval
        const lateApproval = lateApprovalData.map((user, index) => {
            return {
                id: index + 1,
                APP_NAME: provApp.APP_NAME,
                FIRST_NAME: user.FIRST_NAME || "",
                LAST_NAME: user.LAST_NAME || "",
                EMAIL_ADDRESS: user.EMAIL_ADDRESS || "",
                ROLE_NAME: user.ROLE_NAME || "",
                DATE_APPROVED: user.DATE_APPROVED || "",
                DATE_GRANTED: user.DATE_GRANTED || "",
            };
        });

        setProvisioningRowsDetails(allNewUsers);
        setProvisioningMissingDocsRowsDetails(missingDocuments);
        setProvisioningLateApprovalRowsDetails(lateApproval);

        setOpenProvisioningModal(true);
    };

    const handleTerminationClick = (event, app) => {

        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
        setSelectedApp(app);

        // Filter the appsProvisioning array based on the selected app
        const filteredApps = appsTermination.filter(terminationApp => terminationApp.id === app.appID);

        const allTermedUsers = filteredApps.map(provision => {
            return provision.data.removal_per_app.map((user, index) => {
                return {
                    id: index + 1,
                    APP_NAME: provision.APP_NAME,
                    FIRST_NAME: user.FIRST_NAME,
                    LAST_NAME: user.LAST_NAME,
                    EMAIL_ADDRESS: user.EMAIL_ADDRESS,
                    ROLE_NAME: user.ROLE_NAME,
                    DATE_REVOKED: user.DATE_REVOKED,
                };
            });
        }).flat();  // .flat() is used to flatten the array of arrays into a single array

        const lateRemoval = filteredApps.map(term => {
            return term.data.late_removal.map((user, index) => {
                return {
                    id: index + 1,
                    APP_NAME: term.APP_NAME,
                    FIRST_NAME: user.FIRST_NAME,
                    LAST_NAME: user.LAST_NAME,
                    ROLE_NAME: user.ROLE_NAME,
                    SYSTEM_TERMED_DATE: user.SYSTEM_TERMED_DATE,
                    HR_TERMED_DATE: user.HR_TERMED_DATE
                };
            });
        }).flat();  // .flat() is used to flatten the array of arrays into a single array

        // Set the filtered provisioning rows to be used in your modal
        setTerminationRowsDetails(allTermedUsers);
        setTerminationLateRemovalRowsDetails(lateRemoval)

        setOpenTerminationModal(true)

    };


    const renderActionButton = (params) => {
        const app = params.row;

        return (
            <ThemeProvider theme={theme}>
                <mui.Box sx={{ display: 'flex' }}>
                <Tooltip title="View Details" arrow>
                    <mui.IconButton
                            sx={{ 
                                color: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                            }}
                        size="small"
                        onClick={(event) => handleClick(event, app)}
                    >
                        <LaunchIcon sx={{ fontSize: '18px' }} />
                    </mui.IconButton>
                </Tooltip>
                </mui.Box>
            </ThemeProvider>
        );
    };

    const renderProvActionButton = (params) => {
        const app = params.row;

        return (
            <ThemeProvider theme={theme}>
                <mui.Box sx={{ display: 'flex' }}>
                    <Tooltip title="View Details" arrow>
                    <mui.IconButton
                            sx={{ 
                                color: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                            }}
                        size="small"
                        onClick={(event) => handleProvisioningClick(event, app)}
                    >
                            <LaunchIcon sx={{ fontSize: '18px' }} />
                    </mui.IconButton>
                </Tooltip>
                </mui.Box>
            </ThemeProvider>
        );
    };

    const renderTermActionButton = (params) => {
        const app = params.row;

        return (
            <ThemeProvider theme={theme}>
                <mui.Box sx={{ display: 'flex' }}>
                    <Tooltip title="View Details" arrow>
                    <mui.IconButton
                            sx={{ 
                                color: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                            }}
                        size="small"
                        onClick={(event) => handleTerminationClick(event, app)}
                    >
                        <LaunchIcon sx={{ fontSize: '18px' }} />
                    </mui.IconButton>
                </Tooltip>
                </mui.Box>
            </ThemeProvider>
        );
    };

    // Add edit and action buttons to columns
    const columnsWithActions = [
        ...columns,
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: renderActionButton,
        },
    ];

    const columnsWithActionsProvisioning = [
        ...provisioningcolumns,
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: renderProvActionButton,
        },
    ];

    const columnsWithActionsTermination = [
        ...terminationcolumns,
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: renderTermActionButton,
        },
    ];

    // Add the missing function
    function createData(name, policy, configured, result) {
        return { name, policy, configured, result };
    }

    // Add missing handlers
    const handleClosePasswordModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenPasswordModal(true);
        } else {
            setOpenPasswordModal(false);
        }
    };

    const handleCloseProvisioningModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenProvisioningModal(true);
        } else {
            setOpenProvisioningModal(false);
        }
    };

    const handleCloseTerminationModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenTerminationModal(true);
        } else {
            setOpenTerminationModal(false);
        }
    };
    
    // Add the missing tabs variable
    const tabs = [
        {
            value: '1',
            label: (<mui.Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', py: 0.5 }}>
                <SecurityIcon sx={{ mr: 0.8, fontSize: 18 }} /> Authentication
                {authCount > 0 && <ComplianceBadge count={authCount} type="violation">{authCount}</ComplianceBadge>}
            </mui.Box>),
            content: (
                <div>
                    <mui.Grid container spacing={4} sx={{ marginBottom: '32px', mt: 2 }}>
                        <mui.Grid item xs={12} md={4}>
                            <ChartContainer>
                                <mui.Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.9rem' }}>Authentication Compliance Status</mui.Typography>
                                <mui.Box sx={{ height: 290, display: 'flex', justifyContent: 'center' }}>
                                    <PieChart
                                        series={[
                                            {
                                                data: compliancePieData,
                                                innerRadius: 55,
                                                outerRadius: 90,
                                                paddingAngle: 1,
                                                cornerRadius: 3,
                                                startAngle: -90,
                                                endAngle: 270,
                                            },
                                        ]}
                                        height={290}
                                        colors={[theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main]}
                                        slotProps={{
                                            legend: {
                                                direction: 'column',
                                                position: { vertical: 'middle', horizontal: 'right' },
                                                padding: 0,
                                                itemMarkWidth: 10,
                                                itemMarkHeight: 10,
                                                markGap: 4,
                                                itemGap: 6,
                                                fontSize: 12
                                            },
                                        }}
                                    />
                                </mui.Box>
                            </ChartContainer>
                        </mui.Grid>
                        <mui.Grid item xs={12} md={8}>
                            <mui.Grid container spacing={2}>
                                <mui.Grid item xs={12} md={4}>
                                    <StatsCard>
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 40, 
                                                    height: 40, 
                                                    margin: '0 auto',
                                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                                    color: theme.palette.success.main
                                                }}
                                            >
                                                <VerifiedIcon sx={{ fontSize: '1.2rem' }} />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 1 }}>{compliantCount}</StatsValue>
                                            <StatsLabel>Compliant</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                        </mui.Grid>
                                <mui.Grid item xs={12} md={4}>
                                    <StatsCard>
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 40, 
                                                    height: 40, 
                                                    margin: '0 auto',
                                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                    color: theme.palette.warning.main
                                                }}
                                            >
                                                <WarningAmberIcon sx={{ fontSize: '1.2rem' }} />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 2 }}>{authPolicyViolationCount}</StatsValue>
                                            <StatsLabel>Policy Exceptions</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                                </mui.Grid>
                                <mui.Grid item xs={12} md={4}>
                                    <StatsCard>
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 40, 
                                                    height: 40, 
                                                    margin: '0 auto',
                                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                                    color: theme.palette.info.main
                                                }}
                                            >
                                                <CancelIcon sx={{ fontSize: '1.2rem' }} />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 2 }}>{authIncompleteCount}</StatsValue>
                                            <StatsLabel>Incomplete Setup</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                                </mui.Grid>
                                <mui.Grid item xs={12}>
                                    <DataCard>
                                        <mui.CardContent sx={{ padding: '16px 24px' }}>
                                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <mui.Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Compliance Issues</mui.Typography>
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <mui.TextField
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Search..."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <mui.InputAdornment position="start">
                                                                    <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                                                </mui.InputAdornment>
                                                            ),
                                                            sx: { fontSize: '0.8rem', height: '36px' }
                                                        }}
                                                        sx={{ mr: 1, width: 180 }}
                                                    />
                                                </mui.Box>
                                            </mui.Box>
                                            
                                            <mui.Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <mui.Chip
                                                    label="All"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'All' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('All')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'All' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'All' ? theme.palette.primary.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'All' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                <mui.Chip
                                                    label="Compliant"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'Compliant' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('Compliant')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'Compliant' ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'Compliant' ? theme.palette.success.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'Compliant' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.success.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.success.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                <mui.Chip
                                                    label="Needs Review"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'Needs Review' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('Needs Review')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'Needs Review' ? alpha(theme.palette.warning.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'Needs Review' ? theme.palette.warning.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'Needs Review' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.warning.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.warning.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                {selectedStatusFilter !== 'All' && (
                                                    <mui.Chip
                                                        label="Reset"
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={resetFilters}
                                                        onDelete={resetFilters}
                                                        deleteIcon={<RefreshIcon fontSize="small" />}
                                                        sx={{ 
                                                            borderColor: alpha(theme.palette.text.secondary, 0.3),
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.text.secondary, 0.05)
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </mui.Box>
                                            
                                            <Suspense fallback={
                                                <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                                    <LogoLoader size={40} color={theme.palette.primary.main} />
                                                </mui.Box>
                                            }>
                        <DataTableContainer>
                            <StyledDataGrid>
                                <DataTable
                                    rows={filteredRows.auth || filteredAuthRows}
                                    columns={columnsWithActions}
                                    height={430}
                                />
                            </StyledDataGrid>
                        </DataTableContainer>
                    </Suspense>
                                        </mui.CardContent>
                                    </DataCard>
                                </mui.Grid>
                            </mui.Grid>
                        </mui.Grid>
                    </mui.Grid>
                </div>
            ),
        },
        {
            value: '2',
            label: (<mui.Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', py: 0.5 }}>
                <PersonAddIcon sx={{ mr: 0.8, fontSize: 18 }} /> Access Provisioning
                {(provNoDocCount + provLateCount) > 0 && <ComplianceBadge count={provNoDocCount + provLateCount} type="violation">{provNoDocCount + provLateCount}</ComplianceBadge>}
            </mui.Box>),
            content: (
                <div>
                    <mui.Grid container spacing={3} sx={{ marginBottom: '20px' }}>
                        <mui.Grid item xs={12} md={4}>
                            <ChartContainer>
                                <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Provisioning Compliance Status</mui.Typography>
                                
                                {/* Add Date Range Filter */}
                                <mui.Box 
                                    sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        mb: 3, 
                                        p: 2, 
                                        bgcolor: 'rgba(0,0,0,0.02)',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0,0,0,0.08)'
                                    }}
                                >
                                    <mui.Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.8rem' }}>
                                        Filter by Date Range
                                    </mui.Typography>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <mui.Typography variant="caption" sx={{ width: 80, fontSize: '0.75rem' }}>
                                            Start Date:
                                        </mui.Typography>
                                        <mui.TextField
                                            type="date"
                                            size="small"
                                            value={formatDateForAPI(provisioningDateRange.startDate)}
                                            onChange={(e) => {
                                                try {
                                                    // Only proceed if the value is a valid date string
                                                    if (e.target.value && e.target.value.trim()) {
                                                        const newDate = new Date(e.target.value);
                                                        handleProvisioningDateRangeChange('startDate', newDate);
                                                    }
                                                } catch (error) {
                                                    console.error("Error parsing date:", error);
                                                }
                                            }}
                                            InputProps={{ 
                                                sx: { fontSize: '0.8rem', height: '32px' } 
                                            }}
                                            fullWidth
                                        />
                                    </mui.Box>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <mui.Typography variant="caption" sx={{ width: 80, fontSize: '0.75rem' }}>
                                            End Date:
                                        </mui.Typography>
                                        <mui.TextField
                                            type="date"
                                            size="small"
                                            value={formatDateForAPI(provisioningDateRange.endDate)}
                                            onChange={(e) => {
                                                try {
                                                    // Only proceed if the value is a valid date string
                                                    if (e.target.value && e.target.value.trim()) {
                                                        const newDate = new Date(e.target.value);
                                                        handleProvisioningDateRangeChange('endDate', newDate);
                                                    }
                                                } catch (error) {
                                                    console.error("Error parsing date:", error);
                                                }
                                            }}
                                            InputProps={{ 
                                                sx: { fontSize: '0.8rem', height: '32px' } 
                                            }}
                                            fullWidth
                                        />
                                    </mui.Box>
                                    <mui.Button 
                                        variant="contained" 
                                        size="small" 
                                        onClick={applyProvisioningDateFilter}
                                        sx={{ alignSelf: 'flex-end', fontSize: '0.75rem', mt: 1 }}
                                    >
                                        Apply Filter
                                    </mui.Button>
                                </mui.Box>
                                
                                <mui.Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                    <PieChart
                                        series={[
                                            {
                                                data: provisioningPieData,
                                                innerRadius: 60,
                                                outerRadius: 100,
                                                paddingAngle: 2,
                                                cornerRadius: 4,
                                                startAngle: -90,
                                                endAngle: 270,
                                            },
                                        ]}
                                        height={300}
                                        colors={[theme.palette.success.main, theme.palette.warning.main, theme.palette.warning.main]}
                                        slotProps={{
                                            legend: {
                                                direction: 'column',
                                                position: { vertical: 'middle', horizontal: 'right' },
                                                padding: 0,
                                                itemMarkWidth: 12,
                                                itemMarkHeight: 12,
                                                markGap: 5,
                                                itemGap: 8,
                                            },
                                        }}
                                    />
                                </mui.Box>
                            </ChartContainer>
                        </mui.Grid>
                        <mui.Grid item xs={12} md={8}>
                            <mui.Grid container spacing={3}>
                                <mui.Grid item xs={12} md={6}>
                                    <StatsCard color="warning">
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 56, 
                                                    height: 56, 
                                                    margin: '0 auto',
                                                    bgcolor: provNoDocCount > 0 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.success.main, 0.1), 
                                                    color: theme.palette.warning.main
                                                }}
                                            >
                                                <GppBadIcon />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 2 }}>{provNoDocCount}</StatsValue>
                                            <StatsLabel>No Documentation</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                        </mui.Grid>
                                <mui.Grid item xs={12} md={6}>
                                    <StatsCard color="warning">
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 56, 
                                                    height: 56, 
                                                    margin: '0 auto',
                                                    bgcolor: provLateCount > 0 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.success.main, 0.1), 
                                                    color: theme.palette.warning.main
                                                }}
                                            >
                                                <EventBusyIcon />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 2 }}>{provLateCount}</StatsValue>
                                            <StatsLabel>Late Approval</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                    </mui.Grid>
                                <mui.Grid item xs={12}>
                                    <DataCard>
                                        <mui.CardContent>
                                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>Provisioning Issues</mui.Typography>
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <mui.TextField
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Search..."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <mui.InputAdornment position="start">
                                                                    <SearchIcon fontSize="small" />
                                                                </mui.InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{ mr: 1, width: 200 }}
                                                    />
                                                </mui.Box>
                                            </mui.Box>
                                            
                                            <mui.Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <mui.Chip
                                                    label="All"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'All' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('All')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'All' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'All' ? theme.palette.primary.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'All' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                <mui.Chip
                                                    label="Compliant"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'Compliant' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('Compliant')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'Compliant' ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'Compliant' ? theme.palette.success.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'Compliant' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.success.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.success.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                <mui.Chip
                                                    label="Needs Review"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'Needs Review' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('Needs Review')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'Needs Review' ? alpha(theme.palette.warning.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'Needs Review' ? theme.palette.warning.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'Needs Review' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.warning.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.warning.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                {selectedStatusFilter !== 'All' && (
                                                    <mui.Chip
                                                        label="Reset"
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={resetFilters}
                                                        onDelete={resetFilters}
                                                        deleteIcon={<RefreshIcon fontSize="small" />}
                                                        sx={{ 
                                                            borderColor: alpha(theme.palette.text.secondary, 0.3),
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.text.secondary, 0.05)
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </mui.Box>
                                            
                                            <Suspense fallback={<mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                <LogoLoader size={40} color={theme.palette.primary.main} />
                                             </mui.Box>}>
                        <DataTableContainer>
                            <StyledDataGrid>
                                <DataTable
                                    rows={filteredRows.provisioning || filteredProvisioningRows}
                                    columns={columnsWithActionsProvisioning}
                                    height={430}
                                />
                            </StyledDataGrid>
                        </DataTableContainer>
                    </Suspense>
                                        </mui.CardContent>
                                    </DataCard>
                                </mui.Grid>
                            </mui.Grid>
                        </mui.Grid>
                    </mui.Grid>
                </div>
            ),
        },
        {
            value: '3',
            label: (<mui.Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', py: 0.5 }}>
                <PersonRemoveIcon sx={{ mr: 0.8, fontSize: 18 }} /> Access Termination
                {lateremovalCount > 0 && <ComplianceBadge count={lateremovalCount} type="violation">{lateremovalCount}</ComplianceBadge>}
            </mui.Box>),
            content: (
                <div>
                    <mui.Grid container spacing={3} sx={{ marginBottom: '20px' }}>
                        <mui.Grid item xs={12} md={4}>
                            <ChartContainer>
                                <mui.Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Termination Compliance Status</mui.Typography>
                                
                                {/* Add Date Range Filter */}
                                <mui.Box 
                                    sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        mb: 3, 
                                        p: 2, 
                                        bgcolor: 'rgba(0,0,0,0.02)',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0,0,0,0.08)'
                                    }}
                                >
                                    <mui.Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.8rem' }}>
                                        Filter by Date Range
                                    </mui.Typography>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <mui.Typography variant="caption" sx={{ width: 80, fontSize: '0.75rem' }}>
                                            Start Date:
                                        </mui.Typography>
                                        <mui.TextField
                                            type="date"
                                            size="small"
                                            value={formatDateForAPI(terminationDateRange.startDate)}
                                            onChange={(e) => {
                                                try {
                                                    // Only proceed if the value is a valid date string
                                                    if (e.target.value && e.target.value.trim()) {
                                                        const newDate = new Date(e.target.value);
                                                        handleTerminationDateRangeChange('startDate', newDate);
                                                    }
                                                } catch (error) {
                                                    console.error("Error parsing date:", error);
                                                }
                                            }}
                                            InputProps={{ 
                                                sx: { fontSize: '0.8rem', height: '32px' } 
                                            }}
                                            fullWidth
                                        />
                                    </mui.Box>
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <mui.Typography variant="caption" sx={{ width: 80, fontSize: '0.75rem' }}>
                                            End Date:
                                        </mui.Typography>
                                        <mui.TextField
                                            type="date"
                                            size="small"
                                            value={formatDateForAPI(terminationDateRange.endDate)}
                                            onChange={(e) => {
                                                try {
                                                    // Only proceed if the value is a valid date string
                                                    if (e.target.value && e.target.value.trim()) {
                                                        const newDate = new Date(e.target.value);
                                                        handleTerminationDateRangeChange('endDate', newDate);
                                                    }
                                                } catch (error) {
                                                    console.error("Error parsing date:", error);
                                                }
                                            }}
                                            InputProps={{ 
                                                sx: { fontSize: '0.8rem', height: '32px' } 
                                            }}
                                            fullWidth
                                        />
                                    </mui.Box>
                                    <mui.Button 
                                        variant="contained" 
                                        size="small" 
                                        onClick={applyTerminationDateFilter}
                                        sx={{ alignSelf: 'flex-end', fontSize: '0.75rem', mt: 1 }}
                                    >
                                        Apply Filter
                                    </mui.Button>
                                </mui.Box>
                                
                                <mui.Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                    <PieChart
                                        series={[
                                            {
                                                data: terminationPieData,
                                                innerRadius: 60,
                                                outerRadius: 100,
                                                paddingAngle: 2,
                                                cornerRadius: 4,
                                                startAngle: -90,
                                                endAngle: 270,
                                            },
                                        ]}
                                        height={300}
                                        colors={[theme.palette.success.main, theme.palette.warning.main]}
                                        slotProps={{
                                            legend: {
                                                direction: 'column',
                                                position: { vertical: 'middle', horizontal: 'right' },
                                                padding: 0,
                                                itemMarkWidth: 12,
                                                itemMarkHeight: 12,
                                                markGap: 5,
                                                itemGap: 8,
                                            },
                                        }}
                                    />
                                </mui.Box>
                            </ChartContainer>
                        </mui.Grid>
                        <mui.Grid item xs={12} md={8}>
                            <mui.Grid container spacing={3}>
                                <mui.Grid item xs={12} md={6}>
                                    <StatsCard color="error">
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 56, 
                                                    height: 56, 
                                                    margin: '0 auto',
                                                    bgcolor: lateremovalCount > 0 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.success.main, 0.1), 
                                                    color: theme.palette.error.main
                                                }}
                                            >
                                                <PersonRemoveIcon />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 2 }}>{lateremovalCount}</StatsValue>
                                            <StatsLabel>Late Removal</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                                </mui.Grid>
                                <mui.Grid item xs={12} md={6}>
                                    <StatsCard color="success">
                                        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
                                            <mui.Avatar 
                                                sx={{ 
                                                    width: 56, 
                                                    height: 56, 
                                                    margin: '0 auto',
                                                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                    color: theme.palette.success.main
                                                }}
                                            >
                                                <VerifiedIcon />
                                            </mui.Avatar>
                                            <StatsValue sx={{ mt: 2 }}>{appsTermination.length - lateremovalCount}</StatsValue>
                                            <StatsLabel>Compliant</StatsLabel>
                                        </mui.Box>
                                    </StatsCard>
                                </mui.Grid>
                                <mui.Grid item xs={12}>
                                    <DataCard>
                                        <mui.CardContent>
                                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>Termination Issues</mui.Typography>
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <mui.TextField
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Search..."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <mui.InputAdornment position="start">
                                                                    <SearchIcon fontSize="small" />
                                                                </mui.InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{ mr: 1, width: 200 }}
                                                    />
                                                </mui.Box>
                                            </mui.Box>
                                            
                                            <mui.Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <mui.Chip
                                                    label="All"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'All' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('All')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'All' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'All' ? theme.palette.primary.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'All' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                <mui.Chip
                                                    label="Compliant"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'Compliant' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('Compliant')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'Compliant' ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'Compliant' ? theme.palette.success.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'Compliant' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.success.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.success.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                <mui.Chip
                                                    label="Needs Review"
                                                    size="small"
                                                    variant={selectedStatusFilter === 'Needs Review' ? 'filled' : 'outlined'}
                                                    onClick={() => handleStatusFilterChange('Needs Review')}
                                                    sx={{ 
                                                        bgcolor: selectedStatusFilter === 'Needs Review' ? alpha(theme.palette.warning.main, 0.1) : 'transparent',
                                                        color: selectedStatusFilter === 'Needs Review' ? theme.palette.warning.main : theme.palette.text.primary,
                                                        fontWeight: selectedStatusFilter === 'Needs Review' ? 600 : 400,
                                                        borderColor: alpha(theme.palette.warning.main, 0.3),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.warning.main, 0.1)
                                                        }
                                                    }}
                                                />
                                                {selectedStatusFilter !== 'All' && (
                                                    <mui.Chip
                                                        label="Reset"
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={resetFilters}
                                                        onDelete={resetFilters}
                                                        deleteIcon={<RefreshIcon fontSize="small" />}
                                                        sx={{ 
                                                            borderColor: alpha(theme.palette.text.secondary, 0.3),
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.text.secondary, 0.05)
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </mui.Box>
                                            
                                            <Suspense fallback={<mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                <LogoLoader size={40} color={theme.palette.primary.main} />
                                             </mui.Box>}>
                        <DataTableContainer>
                            <StyledDataGrid>
                                <DataTable
                                    rows={filteredRows.termination || filteredTerminationRows}
                                    columns={columnsWithActionsTermination}
                                    height={430}
                                />
                            </StyledDataGrid>
                        </DataTableContainer>
                    </Suspense>
                                        </mui.CardContent>
                                    </DataCard>
                                </mui.Grid>
                            </mui.Grid>
                        </mui.Grid>
                    </mui.Grid>
                </div>
            ),
        },
        {
            value: '4',
            label: (<mui.Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', py: 0.5 }}>
                <SupervisorAccountIcon sx={{ mr: 0.8, fontSize: 18 }} /> User Access Review
            </mui.Box>),
            content: (
                <div>
                    <mui.Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            p: 6,
                            textAlign: 'center',
                            backgroundColor: '#f8fafc',
                            borderRadius: 3
                        }}
                    >
                        <mui.Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                            User Access Review Coming Soon
                        </mui.Typography>
                        <mui.Typography variant="body1" sx={{ mb: 4, maxWidth: 600 }}>
                            This feature will allow you to review and certify user access across all your applications.
                        </mui.Typography>
                        <mui.Button 
                            variant="contained" 
                            color="primary"
                            startIcon={<SupervisorAccountIcon />}
                            disabled
                        >
                            Request Preview
                        </mui.Button>
                    </mui.Box>
                </div>
            ),
        },
        {
            value: '5',
            label: (<mui.Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', py: 0.5 }}>
                <ShieldIcon sx={{ mr: 0.8, fontSize: 18 }} /> Privileged Access
            </mui.Box>),
            content: (
                <div>
                    {/* Admin Roles Management Section */}
                    <AdminRolesManager apps={apps} theme={theme} />
                </div>
            ),
        },
    ];

    // Add missing provisioningTabs
    const provisioningTabs = [
        {
            value: '1',
            label: (<div>
                New Roles/User Assignment
            </div>),
            content: (
                <div>
                    <Suspense fallback={<div>Loading...</div>}>
                        <DataTable
                            rows={provisioningRowDetails ? provisioningRowDetails : []}
                            columns={provisioningColumnsDetails}
                        />
                    </Suspense>
                </div>
            ),
        },
        {
            value: '2',
            label: (<div>
               No Approval
            </div>),
            content: (
                <div>
                    <Suspense fallback={<div>Loading...</div>}>
                        <DataTable
                            rows={provisioningMissingDocsRowDetails ? provisioningMissingDocsRowDetails : []}
                            columns={provisioningColumnsDetails}
                        />
                    </Suspense>
                </div>
            ),
        },
        {
            value: '3',
            label: (<div>
                Late Approval
            </div>),
            content: (
                <div>
                    <Suspense fallback={<div>Loading...</div>}>
                        <DataTable
                            rows={provisioningLateApprovalRowDetails}
                            columns={provisioningLateApprovalColumnsDetails}
                        />
                    </Suspense>
                </div>
            ),
        },
    ];

    // Add missing terminationTabs
    const terminationTabs = [
        {
            value: '1',
            label: (<div>
                Termination List
            </div>),
            content: (
                <div>
                    <Suspense fallback={<div>Loading...</div>}>
                        <DataTable
                            rows={terminationRowDetails ? terminationRowDetails : []}
                            columns={terminationColumnsDetails}
                        />
                    </Suspense>
                </div>
            ),
        },
        {
            value: '2',
            label: (<div>
                Late Removal
            </div>),
            content: (
                <div>
                    <Suspense fallback={<div>Loading...</div>}>
                        <DataTable
                            rows={terminationLateRemovalDetails ? terminationLateRemovalDetails : []}
                            columns={terminationLateRemovalColumnsDetails}
                        />
                    </Suspense>
                </div>
            ),
        },
    ];

    // Add global styles similar to dashboard
    useEffect(() => {
      const style = document.createElement('style');
      style.textContent = `
        .premium-card {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .premium-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px -2px rgba(0, 0, 0, 0.1);
        }
        .premium-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #42a5f5, #1976d2);
          opacity: 0;
          transition: opacity 0.2s ease;
          z-index: 1;
        }
        .premium-card:hover::after {
          opacity: 1;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }, []);

    // Update the customMainContent layout
    const customMainContent = (
      <ThemeProvider theme={theme}>
        <ResponsiveContainer>
          {isLoading ? (
            <mui.Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '80vh' 
            }}>
              <LogoLoader 
                size={80} 
                message="Loading compliance data..." 
                color={theme.palette.primary.main}
                duration={1.8}
              />
            </mui.Box>
          ) : (
          <mui.Box sx={{ padding: '24px' }}>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        <i className="material-icons">home</i>
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        My Dashboard
                    </mui.Link>
                        <mui.Typography color="text.primary">SOX Compliance</mui.Typography>
            </mui.Breadcrumbs>
                
            <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
              <SecurityIcon sx={{ fontSize: 28, color: 'primary.main', mr: 2 }} />
              <mui.Box>
                <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: '1.5rem' }}>
                  SOX Compliance Dashboard
                </mui.Typography>
                <mui.Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  Monitor and manage your compliance with SOX controls
                </mui.Typography>
              </mui.Box>
            </mui.Box>

            <mui.Box sx={{ 
              backgroundColor: '#f8fafc', 
              borderRadius: 2,
              boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
              padding: '24px',
              mb: 4
            }}>
              <mui.Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mb: 3 
              }}>
                <mui.Button
                  variant="outlined"
                  startIcon={<RefreshIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => setRefreshTrigger(prev => prev + 1)}
                  sx={{ mr: 2, fontSize: '0.8rem', height: '36px' }}
                >
                  Refresh Data
                </mui.Button>
                <mui.Tooltip title="Manage Monitoring">
                  <mui.Button
                    variant="outlined"
                    startIcon={<SettingsRoundedIcon sx={{ fontSize: '1rem' }} />}
                    sx={{ mr: 2, fontSize: '0.8rem', height: '36px' }}
                  >
                    Settings
                  </mui.Button>
                </mui.Tooltip>
                <mui.Tooltip title="Download Compliance Report">
                  <mui.Button
                    variant="contained"
                    startIcon={<DownloadRoundedIcon sx={{ fontSize: '1rem' }} />}
                    sx={{ fontSize: '0.8rem', height: '36px' }}
                  >
                    Export Report
                  </mui.Button>
                </mui.Tooltip>
              </mui.Box>

              <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Cards showing each compliance category */}
                <mui.Grid item xs={12} md={6} lg={3}>
                  <StatsCard 
                    className="premium-card" 
                    onClick={() => {
                      setActiveTab("1");
                      localStorage.setItem('complianceActiveTab', "1");
                    }}
                    sx={{ 
                      position: 'relative',
                      borderLeft: authCount > 0 ? `3px solid ${theme.palette.primary.main}` : 'none',
                      background: authCount > 0 ? `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.03)}, white)` : '#fff',
                      cursor: 'pointer',
                      ...(authCount > 0 && {
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      })
                    }}
                  >
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <mui.Box>
                        <StatsLabel>Authentication</StatsLabel>
                        <StatsValue color={authCount > 0 ? theme.palette.text.primary : theme.palette.success.main}>
                          {authCount > 0 ? `${authCount} Issues` : 'Compliant'}
                        </StatsValue>
                      </mui.Box>
                      <mui.Avatar sx={{ 
                        bgcolor: authCount > 0 ? alpha(theme.palette.grey[200], 0.7) : alpha(theme.palette.success.light, 0.15), 
                        color: authCount > 0 ? theme.palette.text.secondary : theme.palette.success.main,
                        width: 36,
                        height: 36
                      }}>
                        <SecurityIcon sx={{ fontSize: '1.2rem' }} />
                      </mui.Avatar>
                    </mui.Box>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                      <mui.Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                        {compliantCount} of {apps.length} compliant
                      </mui.Typography>
                      {authCount > 0 && (
                        <mui.Button 
                          size="small" 
                          color="inherit"
                          variant="text" 
                          sx={{ 
                            ml: 'auto', 
                            p: '2px 8px', 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent parent card click
                            setActiveTab("1");
                            localStorage.setItem('complianceActiveTab', "1");
                          }}
                        >
                          View Issues
                        </mui.Button>
                      )}
                    </mui.Box>
                  </StatsCard>
                </mui.Grid>
                
                {/* Provisioning Card */}
                <mui.Grid item xs={12} md={6} lg={3}>
                  <StatsCard 
                    className="premium-card" 
                    onClick={() => {
                      setActiveTab("2");
                      localStorage.setItem('complianceActiveTab', "2");
                    }}
                    sx={{ 
                      position: 'relative',
                      borderLeft: provNoDocCount > 0 ? `3px solid ${theme.palette.primary.main}` : 'none',
                      background: provNoDocCount > 0 ? `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.03)}, white)` : '#fff',
                      cursor: 'pointer',
                      ...(provNoDocCount > 0 && {
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      })
                    }}
                  >
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <mui.Box>
                        <StatsLabel>Provisioning</StatsLabel>
                        <StatsValue color={provNoDocCount > 0 ? theme.palette.text.primary : theme.palette.success.main}>
                          {provNoDocCount > 0 ? `${provNoDocCount} Issues` : 'Compliant'}
                        </StatsValue>
                      </mui.Box>
                      <mui.Avatar sx={{ 
                        bgcolor: provNoDocCount > 0 ? alpha(theme.palette.grey[200], 0.7) : alpha(theme.palette.success.light, 0.15), 
                        color: provNoDocCount > 0 ? theme.palette.text.secondary : theme.palette.success.main,
                        width: 36,
                        height: 36
                      }}>
                        <GppBadIcon sx={{ fontSize: '1.2rem' }} />
                      </mui.Avatar>
                    </mui.Box>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                      <mui.Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                        {provNoDocCount + provLateCount} of {appsProvisioning.length} total
                      </mui.Typography>
                      {provNoDocCount > 0 && (
                        <mui.Button 
                          size="small" 
                          color="inherit"
                          variant="text" 
                          sx={{ 
                            ml: 'auto', 
                            p: '2px 8px', 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent parent card click
                            setActiveTab("2");
                            localStorage.setItem('complianceActiveTab', "2");
                          }}
                        >
                          View Issues
                        </mui.Button>
                      )}
                    </mui.Box>
                  </StatsCard>
                </mui.Grid>
                
                {/* Termination Card */}
                <mui.Grid item xs={12} md={6} lg={3}>
                  <StatsCard 
                    className="premium-card" 
                    onClick={() => {
                      setActiveTab("3");
                      localStorage.setItem('complianceActiveTab', "3");
                    }}
                    sx={{ 
                      position: 'relative',
                      borderLeft: lateremovalCount > 0 ? `3px solid ${theme.palette.primary.main}` : 'none',
                      background: lateremovalCount > 0 ? `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.03)}, white)` : '#fff',
                      cursor: 'pointer',
                      ...(lateremovalCount > 0 && {
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      })
                    }}
                  >
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <mui.Box>
                        <StatsLabel>Termination</StatsLabel>
                        <StatsValue color={lateremovalCount > 0 ? theme.palette.text.primary : theme.palette.success.main}>
                          {lateremovalCount > 0 ? `${lateremovalCount} Issues` : 'Compliant'}
                        </StatsValue>
                      </mui.Box>
                      <mui.Avatar sx={{ 
                        bgcolor: lateremovalCount > 0 ? alpha(theme.palette.grey[200], 0.7) : alpha(theme.palette.success.light, 0.15), 
                        color: lateremovalCount > 0 ? theme.palette.text.secondary : theme.palette.success.main,
                        width: 36,
                        height: 36
                      }}>
                        <PersonRemoveIcon />
                      </mui.Avatar>
                    </mui.Box>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                      <mui.Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                        {lateremovalCount} of {appsTermination.length} total
                      </mui.Typography>
                      {lateremovalCount > 0 && (
                        <mui.Button 
                          size="small" 
                          color="inherit"
                          variant="text" 
                          sx={{ 
                            ml: 'auto', 
                            p: '2px 8px', 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent parent card click
                            setActiveTab("3");
                            localStorage.setItem('complianceActiveTab', "3");
                          }}
                        >
                          View Issues
                        </mui.Button>
                      )}
                    </mui.Box>
                  </StatsCard>
                </mui.Grid>
                
                {/* User Access Review Card */}
                <mui.Grid item xs={12} md={6} lg={3}>
                  <StatsCard 
                    className="premium-card" 
                    onClick={() => {
                      setActiveTab("4");
                      localStorage.setItem('complianceActiveTab', "4");
                    }}
                    sx={{ 
                      position: 'relative',
                      borderLeft: apps.length > 0 ? `3px solid ${theme.palette.primary.main}` : 'none',
                      background: apps.length > 0 ? `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.03)}, white)` : '#fff',
                      cursor: 'pointer',
                      ...(apps.length > 0 && {
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      })
                    }}
                  >
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <mui.Box>
                        <StatsLabel>User Access Review</StatsLabel>
                        <StatsValue color={apps.length > 0 ? theme.palette.text.primary : theme.palette.success.main}>
                          {apps.length > 0 ? `${apps.length} Applications` : 'No Applications'}
                        </StatsValue>
                      </mui.Box>
                      <mui.Avatar sx={{ 
                        bgcolor: apps.length > 0 ? alpha(theme.palette.grey[200], 0.7) : alpha(theme.palette.success.light, 0.15), 
                        color: apps.length > 0 ? theme.palette.text.secondary : theme.palette.success.main,
                        width: 36,
                        height: 36
                      }}>
                        <SupervisorAccountIcon sx={{ fontSize: '1.2rem' }} />
                      </mui.Avatar>
                    </mui.Box>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                      <mui.Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                        {apps.length} of {apps.length} applications
                      </mui.Typography>
                      {apps.length > 0 && (
                        <mui.Button 
                          size="small" 
                          color="inherit"
                          variant="text" 
                          sx={{ 
                            ml: 'auto', 
                            p: '2px 8px', 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent parent card click
                            setActiveTab("4");
                            localStorage.setItem('complianceActiveTab', "4");
                          }}
                        >
                          View Issues
                        </mui.Button>
                      )}
                    </mui.Box>
                  </StatsCard>
                </mui.Grid>
              </mui.Grid>

              <mui.Box sx={{ marginBottom: '24px' }}>
                {/* Update TabContext to use activeTab and handleTabChange */}
                <TabContext value={activeTab}>
                    <mui.Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="compliance tabs"
                            sx={{
                                '.MuiTabs-flexContainer': {
                                    gap: '8px',
                                },
                                '.MuiTab-root': {
                                    borderRadius: '6px 6px 0 0',
                                    minHeight: '42px',
                                    py: '6px',
                                }
                            }}
                        >
                            {tabs.map(tab => (
                                <Tab key={tab.value} label={tab.label} value={tab.value} />
                            ))}
                        </TabList>
                    </mui.Box>
                    
                    {tabs.map(tab => (
                        <TabPanel key={tab.value} value={tab.value} sx={{ p: 3, px: 0 }}>
                            {tab.content}
                        </TabPanel>
                    ))}
                </TabContext>
              </mui.Box>
            </mui.Box>

            {/* Modals */}
            <Modal
              open={openPasswordModal}
              size="md"
              onClose={handleClosePasswordModal}
              header={`${selectedApp.APP_NAME} Password Details`}
              body={
                <>
                    <mui.Typography variant="subtitle2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                        Below is the detailed comparison of the password configured in {selectedApp.APP_NAME} against the requirement of the password policy.
                    </mui.Typography>

                    <mui.Box sx={{ marginTop: '16px' }}>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                            <Table aria-label="password policy table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, py: 1.5 }}>Password Parameters</TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, py: 1.5 }}>Policy Requirement</TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, py: 1.5 }}>Configured</TableCell>
                                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, py: 1.5 }}>Result</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {passwordRows.map((row) => (
                                        <TableRow
                                            key={row.name}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row" sx={{ fontSize: '0.75rem', py: 1.5 }}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1.5 }}>{row.policy}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1.5 }}>{row.configured}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1.5 }}>{row.result}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </mui.Box>
                </>
              }
              footer={
                <>
                    <mui.Button 
                        sx={{ fontSize: '0.8rem', textTransform: 'none' }} 
                        onClick={handleClosePasswordModal} 
                        color="primary"
                    >
                        Close
                    </mui.Button>
                </>
              }
            />

            {/* Other modals */}
            <Modal
              open={openProvisioningModal}
              size="lg"
              onClose={handleCloseProvisioningModal}
              header={`${selectedApp.APP_NAME} Provisioning Summary`}
              body={
                <>
                    <mui.Typography variant="subtitle2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                        Below is the detailed summary of the provisioning report for {selectedApp.APP_NAME}.
                    </mui.Typography>

                    <mui.Box sx={{ marginTop: '16px' }}>
                        <DynamicTabs tabs={provisioningTabs} />
                    </mui.Box>
                </>
              }
              footer={
                <>
                    <mui.Button 
                        sx={{ fontSize: '0.8rem', textTransform: 'none' }} 
                        onClick={handleCloseProvisioningModal} 
                        color="primary"
                    >
                        Close
                    </mui.Button>
                </>
              }
            />

            <Modal
              open={openTerminationModal}
              size="lg"
              onClose={handleCloseTerminationModal}
              header={`${selectedApp.APP_NAME} Termination Summary`}
              body={
                <>
                    <mui.Typography variant="subtitle2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                        Below is the detailed summary of the user termination report for {selectedApp.APP_NAME}.
                    </mui.Typography>

                    <mui.Box sx={{ marginTop: '16px' }}>
                        <DynamicTabs tabs={terminationTabs} />
                    </mui.Box>
                </>
              }
              footer={
                <>
                    <mui.Button 
                        sx={{ fontSize: '0.8rem', textTransform: 'none' }} 
                        onClick={handleCloseTerminationModal} 
                        color="primary"
                    >
                        Close
                    </mui.Button>
                </>
              }
            />

            </mui.Box>)}
          </ResponsiveContainer>
        </ThemeProvider>
    );

    return (
        <div>
            <SysOwnerSideBar mainContent={customMainContent} />
        </div>
    );
}

export default SysOwnerCompliance;